import React from 'react';
import './ModernReport.css';
import { 
  MdSecurity, MdShield, MdShowChart, MdAccountBalanceWallet, 
  MdWarning, MdCheckCircle, MdInfo, MdFlag, MdTimeline, MdTrendingUp, MdEmail, MdPhone, MdDescription
} from 'react-icons/md';
import { FaUmbrella, FaSackDollar, FaPiggyBank, FaHandHoldingDollar, FaFileInvoiceDollar } from 'react-icons/fa6';

// 1:1 PIXEL-PERFECT ICONS (Manually Drawn SVGs for reliability)
const IconInsurance = () => (
    <svg width="50" height="50" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 2C7.5 2 4 5.5 4 10H20C20 5.5 16.5 2 12 2Z" fill="white" fillOpacity="0.4" />
        <path d="M12 10V21M12 22C12.5 22 13 21.5 13 21V10H11V21C11 21.5 11.5 22 12 22Z" />
        <circle cx="8" cy="14" r="1.5" fill="white" />
        <circle cx="12" cy="14" r="1.5" fill="white" />
        <circle cx="16" cy="14" r="1.5" fill="white" />
        <path d="M7.5 16V21M11.5 16V21M15.5 16V21" strokeWidth="1.2" />
    </svg>
);

const IconEmergency = () => (
    <svg width="50" height="50" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M17 8H7L6 11H18L17 8Z" fill="white" fillOpacity="0.3" />
        <path d="M19 13C19 11.5 18 11 17 11H7C6 11 5 11.5 5 13C5 16 8 19 12 19C16 19 19 16 19 13Z" fill="white" />
        <circle cx="12" cy="15" r="2.5" stroke="black" strokeWidth="0.8" />
        <path d="M12 14V16" stroke="black" strokeWidth="0.8" />
        <rect x="15" y="16" width="6" height="4" rx="1" fill="white" />
    </svg>
);

const IconSavings = () => (
    <svg width="50" height="50" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M19 12C19 14.5 17 16.5 14.5 16.5H6.5C4 16.5 2 14.5 2 12C2 9.5 4 7.5 6.5 7.5H14.5C17 7.5 19 9.5 19 12Z" fill="white" />
        <path d="M12 3V7.5" strokeWidth="2" />
        <circle cx="15" cy="11" r="1" fill="black" fillOpacity="0.2" />
        <path d="M6 16.5V18.5M15 16.5V18.5" strokeWidth="1.5" />
    </svg>
);

const IconLoan = () => (
    <svg width="50" height="50" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M3 16C3 15 4 14 6 14H18C20 14 21 15 21 16V17C21 18.5 19.5 20 18 20H6C4.5 20 3 18.5 3 17V16Z" fill="white" fillOpacity="0.3" />
        <circle cx="12" cy="9" r="4" fill="white" />
        <circle cx="12" cy="9" r="2" stroke="black" strokeWidth="0.6" />
        <path d="M12 8V10" stroke="black" strokeWidth="0.6" />
    </svg>
);

const IconTax = () => (
    <svg width="50" height="50" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M17 3H7C5.9 3 5 3.9 5 5V19C5 20.1 5.9 21 7 21H17C18.1 21 19 20.1 19 19V5C19 3.9 18.1 3 17 3Z" fill="white" />
        <path d="M8 7H16M8 10H16M8 13H12" stroke="black" strokeWidth="0.8" />
        <rect x="8" y="16" width="8" height="3" rx="1" fill="#e2e8f0" />
        <text x="9.5" y="18.2" fill="black" fontSize="3" fontWeight="900" style={{ fontFamily: 'Arial' }}>TAX</text>
    </svg>
);

const ModernReport = ({ data = {}, calculations = {}, isSummary = false }) => {
    if (!calculations || !calculations.metrics) return null;

    const { metrics = {}, grades = {} } = calculations;
    const score = calculations.healthScore || grades.overall || 46;
    const m = metrics; // Use metrics as the source for 'm' alias
    const p = data.personalInfo || {};
    const isCouple = p.familyType === 'couple';
    const jointSalary = data.jointSalary || {};

    const safeParse = (v) => { 
        if (typeof v === "number") return v; 
        if (!v) return 0; 
        return parseFloat(v.toString().replace(/[₹, \s]/g, "")) || 0; 
    };

    const fmtK = (val) => {
        if (!val) return "0";
        if (val >= 100000) return `${(val / 100000).toFixed(1)}L`;
        if (val >= 1000) return `${Math.round(val / 1000)}k`;
        return val.toString();
    };

    const fmtL = (val) => {
        if (!val) return "0";
        if (val >= 10000000) return `${(val / 10000000).toFixed(1)}Cr`;
        if (val >= 100000) return `${(val / 100000).toFixed(2)}L`;
        if (val >= 1000) return `${Math.round(val / 1000)}k`;
        return val.toString();
    };

    let srVal = 0, eePct = 0, lsPct = 0, dtiVal = 0, emActual = 0, emTarget = 0, lifeActual = 0, lifeTarget = 0;
    let investments = 0, calcEssential = 0, lifestyleSpending = 0, totalEMIs = 0, efPctFunded = 0;
    let combinedMonthly = 0;

    if (isCouple) {
        const sectionB = jointSalary.sectionB || {};
        const sectionC = jointSalary.sectionC || {};
        const sectionD = jointSalary.sectionD || {};
        const sectionE = jointSalary.sectionE || {};
        const sectionF = jointSalary.sectionF || {};
        const sectionG = jointSalary.sectionG || {};

        const hSalary = safeParse(sectionB.husbandSalary) || 0;
        const wSalary = safeParse(sectionB.wifeSalary) || 0;
        const hOther = safeParse(sectionB.husbandOtherIncome) || 0;
        const wOther = safeParse(sectionB.wifeOtherIncome) || 0;

        const hTotal = hSalary + hOther;
        const wTotal = wSalary + wOther;
        combinedMonthly = hTotal + wTotal;
        const combinedAnnual = combinedMonthly * 12;

        const hl = safeParse(sectionF.homeLoanEMI) || safeParse(sectionC.homeLoanEMI) || 0;
        const pl = safeParse(sectionF.personalLoanEMI) || safeParse(sectionC.personalLoanEMI) || 0;
        const cl = safeParse(sectionF.carLoanEMI) || safeParse(sectionC.carLoanEMI) || 0;

        const groceriesUtilities = (safeParse(sectionC.groceries) || 0) + (safeParse(sectionC.rent) || 0) + (safeParse(sectionC.schoolFees) || 0) + (safeParse(sectionC.otherExpenses) || 0);
        const transport = (safeParse(sectionC.transport) || 0) + cl;
        const dining = safeParse(sectionC.dining) || 0;
        const shopping = safeParse(sectionC.shopping) || 0;

        const insurance = (safeParse(sectionC.healthInsurance) || 0) + 
                          (safeParse(sectionE.husbandLifePremium) || 0) + 
                          (safeParse(sectionE.wifeLifePremium) || 0) + 
                          (safeParse(sectionE.husbandHealthPremium) || 0) + 
                          (safeParse(sectionE.wifeHealthPremium) || 0);

        investments = (safeParse(sectionD.husbandEPF) || 0) + (safeParse(sectionD.wifeEPF) || 0) +
                      (safeParse(sectionD.husbandPPF) || 0) + (safeParse(sectionD.wifePPF) || 0) +
                      (safeParse(sectionD.husbandMutualFund) || 0) + (safeParse(sectionD.wifeMutualFund) || 0) +
                      (safeParse(sectionD.husbandELSS) || 0) + (safeParse(sectionD.wifeELSS) || 0) +
                      (safeParse(sectionD.husbandNPS) || 0) + (safeParse(sectionD.wifeNPS) || 0) +
                      (safeParse(sectionD.husbandFD) || 0) + (safeParse(sectionD.wifeFD) || 0) +
                      (safeParse(sectionD.husbandOtherInvest) || 0) + (safeParse(sectionD.wifeOtherInvest) || 0);

        srVal = combinedMonthly > 0 ? (investments / combinedMonthly) * 100 : 0;
        calcEssential = groceriesUtilities + (safeParse(sectionC.transport) || 0) + insurance + hl;
        eePct = combinedMonthly > 0 ? (calcEssential / combinedMonthly) * 100 : 0;
        lifestyleSpending = dining + shopping;
        lsPct = combinedMonthly > 0 ? (lifestyleSpending / combinedMonthly) * 100 : 0;
        totalEMIs = hl + pl + cl;
        dtiVal = combinedMonthly > 0 ? (totalEMIs / combinedMonthly) * 100 : 0;

        emActual = safeParse(sectionG.emergencyFund) || 0;
        const jointExpenses = groceriesUtilities + (safeParse(sectionC.transport) || 0) + dining + shopping + insurance + hl + pl + cl;
        const monthlyExpenses = jointExpenses || (combinedMonthly * 0.5) || 50000;
        emTarget = monthlyExpenses * 6;
        efPctFunded = emTarget > 0 ? (emActual / emTarget) * 100 : 0;

        lifeActual = (safeParse(sectionE.husbandLifeCover) || 0) + (safeParse(sectionE.wifeLifeCover) || 0);
        lifeTarget = Math.max(25000000, combinedAnnual * 10);
    }
    
    const cleanCityName = (val) => {
        if (!val) return "";
        const lower = val.toString().toLowerCase().trim();
        if (lower === "metro" || lower === "non_metro" || lower === "non-metro" || lower === "tier2" || lower === "tier3" || lower === "rural") {
            if (lower === "tier2") return "Tier 2";
            if (lower === "tier3") return "Tier 3";
            if (lower === "non_metro" || lower === "non-metro") return "Non-Metro";
            return val.toString().charAt(0).toUpperCase() + val.toString().slice(1);
        }
        return val.toString().replace(/(?:\s*\()?metro\)?/i, "").trim();
    };

    const capitalize = (s) => s ? s.trim().split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ') : "";

    let name = capitalize((p.firstName || "VALUED") + " " + (p.lastName || "CLIENT"));
    let displayName = name;
    let salutationName = capitalize(p.firstName || "Client");
    let displayAge = `${p.age || '32'} Years`;
    let displayCity = cleanCityName(p.cityType || p.city || p.address || "Pune");
    displayCity = displayCity.split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
    
    let coupleSummaryText1 = "";
    let coupleSummaryText2 = "";
    let coupleStrengthsList = [];

    if (isCouple) {
        const hName = capitalize(jointSalary.sectionA?.husbandName || "");
        const wName = capitalize(jointSalary.sectionA?.wifeName || "");
        displayName = `${hName} & ${wName}`;
        name = displayName;
        
        const hFirstName = hName.split(' ')[0];
        const wFirstName = wName.split(' ')[0];
        salutationName = `${hFirstName} & ${wFirstName}`;
        
        const hAge = jointSalary.sectionA?.husbandAge || "";
        const wAge = jointSalary.sectionA?.wifeAge || "";
        displayAge = `${hAge} Yrs (Husband) & ${wAge} Yrs (Wife)`;
        displayCity = cleanCityName(jointSalary.sectionA?.cityType || p.cityType || p.city || p.address || "Pune");

        const sectionA = jointSalary.sectionA || {};
        const sectionB = jointSalary.sectionB || {};
        const sectionC = jointSalary.sectionC || {};
        const sectionD = jointSalary.sectionD || {};
        const sectionE = jointSalary.sectionE || {};
        const sectionF = jointSalary.sectionF || {};
        const sectionG = jointSalary.sectionG || {};
        
        const getProf = (salary, otherInc, otherIncType) => {
            const hasSal = parseFloat(salary) > 0;
            const hasOth = parseFloat(otherInc) > 0;
            if (hasSal && hasOth && otherIncType) return `salaried + ${otherIncType.toLowerCase()}`;
            if (hasSal) return 'salaried';
            if (hasOth && otherIncType) return otherIncType.toLowerCase();
            return 'salaried';
        };

        const hProf = getProf(sectionB.husbandSalary, sectionB.husbandOtherIncome, sectionB.husbandOtherIncomeType);
        const wProf = getProf(sectionB.wifeSalary, sectionB.wifeOtherIncome, sectionB.wifeOtherIncomeType);

        const formatLakh = (val) => {
            const num = parseFloat(val) || 0;
            if (num >= 100000) return `₹${(num / 100000).toFixed(2).replace(/\.00$/, '')}L/month`;
            if (num >= 1000) return `₹${(num / 1000).toFixed(0)}k/month`;
            return `₹${num}/month`;
        };

        const hIncStr = formatLakh(sectionB.husbandSalary || sectionB.husbandOtherIncome);
        const wIncStr = formatLakh(sectionB.wifeSalary || sectionB.wifeOtherIncome);

        const isDual = (parseFloat(sectionB.husbandSalary) > 0 || parseFloat(sectionB.husbandOtherIncome) > 0) &&
                       (parseFloat(sectionB.wifeSalary) > 0 || parseFloat(sectionB.wifeOtherIncome) > 0);
        const coupleType = isDual ? "dual-income couple" : "single-income couple";
        const city = cleanCityName(sectionA.cityType || p.cityType || p.city || p.address || "Pune");

        const homeLoanEMI = parseFloat(sectionC.homeLoanEMI) || parseFloat(sectionF.homeLoanEMI) || 0;
        const hasHomeLoan = homeLoanEMI > 0 || parseFloat(sectionF.homeLoanOutstanding) > 0;
        const rentPaid = parseFloat(sectionC.rent) || 0;
        const homePart = hasHomeLoan 
            ? "own a joint home (with a loan)" 
            : (rentPaid > 0 ? "live in a rented home" : "are building their financial foundation");

        const hasHealthIns = (parseFloat(sectionE.husbandHealthCover) > 0 || parseFloat(sectionE.wifeHealthCover) > 0 || parseFloat(sectionE.husbandHealthPremium) > 0 || parseFloat(sectionE.wifeHealthPremium) > 0 || parseFloat(sectionC.healthInsurance) > 0);
        const healthPart = hasHealthIns ? "have health insurance" : "have basic health protection";

        const activeInvest = [];
        if (parseFloat(sectionD.husbandEPF) > 0 || parseFloat(sectionD.wifeEPF) > 0) activeInvest.push("EPF");
        if (parseFloat(sectionD.husbandPPF) > 0 || parseFloat(sectionD.wifePPF) > 0) activeInvest.push("PPF");
        if (parseFloat(sectionD.husbandMutualFund) > 0 || parseFloat(sectionD.wifeMutualFund) > 0 || parseFloat(sectionD.husbandELSS) > 0 || parseFloat(sectionD.wifeELSS) > 0) activeInvest.push("Mutual Funds");
        if (parseFloat(sectionD.husbandNPS) > 0 || parseFloat(sectionD.wifeNPS) > 0) activeInvest.push("NPS");
        if (parseFloat(sectionD.husbandFD) > 0 || parseFloat(sectionD.wifeFD) > 0) activeInvest.push("FDs");

        let savePart = "";
        if (activeInvest.length === 0) {
            savePart = "are planning to start saving";
        } else if (activeInvest.length === 1) {
            savePart = `save via ${activeInvest[0]}`;
        } else if (activeInvest.length === 2) {
            savePart = `save via ${activeInvest[0]} and ${activeInvest[1]}`;
        } else {
            savePart = `save via ${activeInvest.slice(0, -1).join(', ')}, and ${activeInvest[activeInvest.length - 1]}`;
        }

        const combinedIncomeNum = (parseFloat(sectionB.husbandSalary) || 0) + (parseFloat(sectionB.husbandOtherIncome) || 0) + 
                                  (parseFloat(sectionB.wifeSalary) || 0) + (parseFloat(sectionB.wifeOtherIncome) || 0);
        
        const formatCombinedLakh = (val) => {
            const num = parseFloat(val) || 0;
            if (num >= 100000) return `₹${(num / 100000).toFixed(2).replace(/\.00$/, '')} lakhs`;
            return `₹${num.toLocaleString('en-IN')}`;
        };
        const combinedIncomeStr = formatCombinedLakh(combinedIncomeNum);

        const gaps = [];
        const lifeCover = (parseFloat(sectionE.husbandLifeCover) || 0) + (parseFloat(sectionE.wifeLifeCover) || 0);
        if (lifeCover <= 0) gaps.push("zero life insurance");

        const emergency = parseFloat(sectionG.emergencyFund) || 0;
        if (emergency <= 0) gaps.push("no emergency fund");

        const personalLoanOutstanding = parseFloat(sectionF.personalLoanOutstanding) || 0;
        if (personalLoanOutstanding > 0) {
            const rate = sectionF.personalLoanRate || "15";
            const amt = personalLoanOutstanding >= 100000 
                ? `₹${(personalLoanOutstanding / 100000).toFixed(1).replace(/\.0$/, '')}L` 
                : `₹${(personalLoanOutstanding / 1000).toFixed(0)}k`;
            gaps.push(`a joint personal loan of ${amt} at ${rate}%`);
        }

        const gapsStr = gaps.length > 0 
            ? `They are disciplined but have critical gaps: ${gaps.join(', ')}.`
            : "They are disciplined and have set up their basic protections well.";

        coupleSummaryText1 = `${hName} (${hAge}, ${hProf}, ${hIncStr}) and ${wName} (${wAge}, ${wProf}, ${wIncStr}) are a ${coupleType} in ${city}. They ${homePart}, ${healthPart}, and ${savePart}. Their combined monthly income is ${combinedIncomeStr}.`;
        
        coupleSummaryText2 = `${gapsStr} Their financial health score today is ${grades.overall || score}/100. This report shows exactly how to move to 80+.`;

        // Build coupleStrengthsList
        const hSalary = safeParse(sectionB.husbandSalary) || 0;
        const wSalary = safeParse(sectionB.wifeSalary) || 0;
        const hOther = safeParse(sectionB.husbandOtherIncome) || 0;
        const wOther = safeParse(sectionB.wifeOtherIncome) || 0;
        const hTotal = hSalary + hOther;
        const wTotal = wSalary + wOther;
        const combinedMonthly = hTotal + wTotal;

        const hl = safeParse(sectionF.homeLoanEMI) || safeParse(sectionC.homeLoanEMI) || 0;

        // 1. Dual Income Stability
        if (isDual) {
            coupleStrengthsList.push({ t: "Dual Income Stability", d: `Two earners mean one job loss does not break you. Both of you are actively contributing to the household income (Husband: ₹${hTotal.toLocaleString('en-IN')}/mo, Wife: ₹${wTotal.toLocaleString('en-IN')}/mo), providing a highly resilient financial safety net.` });
        } else {
            coupleStrengthsList.push({ t: "Single Income Focus", d: `A steady income source of ₹${combinedMonthly.toLocaleString('en-IN')}/month is the foundation of your plan. Focusing on career growth and building a robust emergency fund is key to backing up this single engine.` });
        }

        // 2. Low Lifestyle Spending
        if (lsPct <= 20) {
            coupleStrengthsList.push({ t: `Low Lifestyle Spending (${lsPct.toFixed(0)}%)`, d: "You enjoy life without overspending. Keeping combined lifestyle spending optimized shows exceptional financial discipline, allowing you to route more towards wealth creation." });
        } else {
            coupleStrengthsList.push({ t: `Lifestyle Spending Control (${lsPct.toFixed(0)}%)`, d: "Your combined lifestyle spending is slightly above benchmark. Keeping this optimized is key to your wealth building strategy." });
        }

        // 3. Home Loan
        const homeLoanOutstanding = parseFloat(sectionF.homeLoanOutstanding) || 0;
        const principalRatio = homeLoanOutstanding > 0 ? Math.min(0.55, Math.max(0.35, (hl * 12) / (homeLoanOutstanding + hl * 120))) : 0.5;
        const annualPrincipal = hl * 12 * principalRatio;
        const section24Interest = Math.min(400000, hl * 12 * (1 - principalRatio));
        const section80CPrincipal = Math.min(300000, annualPrincipal);
        const totalHomeDeduction = section24Interest + section80CPrincipal;

        if (hasHomeLoan) {
            coupleStrengthsList.push({ t: "Home Loan as Wealth Tool", d: `₹${(hl/1000).toFixed(0)}k EMI builds equity + joint tax deduction (₹${(totalHomeDeduction/100000).toFixed(1)}L/year combined). Your home loan EMI is not just an expense; it is forced savings building real estate equity.` });
        } else {
            coupleStrengthsList.push({ t: "Zero Home Loan Liabilities", d: "You enjoy the ultimate debt freedom with zero home loan liabilities. This gives you a clean slate and massive capacity to redirect your monthly surplus directly into high-growth wealth creators." });
        }

        // 4. No Credit Card Debt
        const ccOS = parseFloat(sectionF.creditCardOutstanding) || 0;
        const ccEMI = parseFloat(sectionC.creditCardEMI) || parseFloat(sectionF.creditCardEMI) || 0;
        const hasNoCCDebt = ccOS === 0 && ccEMI === 0;

        if (hasNoCCDebt) {
            coupleStrengthsList.push({ t: "No Credit Card Debt", d: "You carry no high-interest revolving debt. Excellent. By keeping your short-term liabilities at zero, you avoid paying 36-42% interest rates that destroy wealth." });
        } else {
            coupleStrengthsList.push({ t: "Credit Card Action Plan", d: `Resolve ₹${ccOS.toLocaleString('en-IN')} outstanding card dues to save interest. Resolving this high-interest debt quickly is a high priority to protect your monthly savings.` });
        }

        // 5. Employer Health Cover
        const combinedCover = (parseFloat(sectionE.husbandHealthCover) || 0) + (parseFloat(sectionE.wifeHealthCover) || 0);

        if (combinedCover > 0) {
            coupleStrengthsList.push({ t: "Employer Health Insurance", d: `₹${(combinedCover/100000).toFixed(0)}L combined base cover (needs family floater top-up). Having employer-sponsored health insurance protects your hard-earned savings from unexpected medical emergencies.` });
        } else {
            coupleStrengthsList.push({ t: "Family Health Protection", d: "Active health insurance plan is running to shield family savings from unexpected hospitalisation costs. Securing a base cover is a smart protective decision." });
        }
    }
    
    const fmt = (v) => Math.round(v || 0).toLocaleString('en-IN');

    const HighlightBox = ({ children, color = '#1a7a5e' }) => (
        <div style={{ background: '#f8fafc', borderLeft: `6px solid ${color}`, padding: '25px', borderRadius: '8px', marginBottom: '25px', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }}>
            {children}
        </div>
    );

    // REUSABLE COMPONENTS
    const PageHeader = ({ section, title }) => (
        <div className="report-header-refined">
            <div className="header-left">
                <span className="section-pill">SECTION {section}</span>
                <h1 className="page-title">{title}</h1>
            </div>
            <div className="header-brand">
                <img src="/Yathartha-Logo.png" alt="Yathartha Logo" className="header-logo-img" />
            </div>
            <div className="header-accent-line"></div>
        </div>
    );

    const PageFooter = ({ num, dark, customText = "" }) => (
        <div className={`report-footer-refined ${dark ? 'footer-dark' : ''}`} style={{ position: 'absolute', bottom: '8mm', left: '20mm', right: '20mm' }}>
            <div className="footer-line"></div>
            <div className="footer-content">
                <span className="footer-client">
                    {customText || (name ? name.toUpperCase() : "VALUED CLIENT")}
                </span>
                <span className="page-pill"><span>{num}</span></span>
            </div>
        </div>
    );



    const GapEntry = ({ id, title, severity, color, desc, icon }) => (
        <div className="gap-entry">
            <div className="gap-card-box">
                <div className="gap-top-banner" style={{ background: color }}>{severity}</div>
                <div className="gap-body-content" style={{ background: color, filter: 'brightness(1.15)' }}>
                    <div className="gap-icon-wrapper" style={{ fontSize: '32px' }}>{icon}</div>
                    <div className="gap-info-wrapper">
                        <div className="gap-stat" style={{ fontSize: '16px', fontWeight: '800' }}>{title}</div>
                        <p className="gap-commentary" style={{ fontSize: '12px', opacity: 0.9, lineHeight: '1.4', margin: '5px 0 0' }}>{desc}</p>
                    </div>
                </div>
            </div>
            <div className="flow-node"></div>
            <div className="severity-pill" style={{ color: color, borderColor: color }}>{severity}</div>
        </div>
    );

    const GradeBadge = ({ grade }) => {
        const color = grade === 'A' ? '#1a7a5e' : grade === 'B' ? '#2471a3' : grade === 'C' ? '#e67e22' : '#c0392b';
        return <span style={{ background: color, color: 'white', padding: '4px 10px', borderRadius: '4px', fontWeight: 800, fontSize: '12px' }}>GRADE {grade}</span>;
    };

    const StatusIcon = ({ score }) => score > 80 ? <MdCheckCircle style={{ color: '#1a7a5e' }} /> : <MdWarning style={{ color: '#e67e22' }} />;

    return (
        <div id="modern-report-content" className="modern-report-wrapper">
            
            {/* PAGE 1: COVER */}
            <div className="report-page cover-page-premium">
                <div className="cover-top">
                    <img src="/Yathartha-Logo.png" alt="Yathartha Logo" className="cover-logo-img" />
                </div>
                <div className="cover-main">
                    <div className="accent-bar"></div>
                    <h1 className="main-title">COMPREHENSIVE<br/>FINANCIAL<br/>DIAGNOSIS REPORT</h1>
                    <p className="main-subtitle">Gap Analysis, Risk Assessment & Strategic Roadmap</p>
                </div>
                <div className="cover-footer-accent">
                    <div className="trust-seal">
                        <MdSecurity />
                        <span>BANK-GRADE ANALYSIS</span>
                    </div>
                </div>
                <div className="cover-info-grid">
                    <div className="info-item">
                        <label>PREPARED FOR</label>
                        <div className="value">{name}</div>
                    </div>
                    <div className="info-item">
                        <label>OVERALL SCORE</label>
                        <div className="value" style={{ color: '#1a7a5e' }}>{score} / 100</div>
                    </div>
                </div>
                <PageFooter num="01" />
            </div>

            {/* PAGE 2: EXECUTIVE SUMMARY */}
            <div className="report-page">
                <div style={{ marginBottom: '25px' }}>
                    <h1 className="gaps-page-title" style={{ fontSize: '36px', color: '#1a7a5e' }}>
                        {isCouple ? 'EXECUTIVE SUMMARY (Joint Family Edition)' : 'EXECUTIVE SUMMARY'}
                    </h1>
                    <p className="summary-subtitle">Your Financial Story at a Glance - {name} | {new Date().toLocaleString('en-IN', { month: 'long', year: 'numeric' })}</p>
                </div>

                <div className="premium-story-card">
                    <div className="story-badge">YOUR STORY</div>
                    <h2>{isCouple ? 'Your Family Story in 60 Seconds' : 'The 60-Second Verdict'}</h2>
                    <div className="story-content">
                        {isCouple ? (
                            <>
                                <p>
                                    {coupleSummaryText1}
                                </p>
                                <p>
                                    <strong>Your Verdict:</strong> {coupleSummaryText2}
                                </p>
                            </>
                        ) : (
                            <>
                                <p>
                                    {displayName} is a {p.age || '30'}-year-old {p.incomeType === 'business' ? 'business owner' : 'salaried professional'} {displayCity ? `in ${displayCity}` : ''} earning ₹{fmt(metrics.totalMonthlyIncome)} per month. 
                                    {p.gender === 'female' ? 'She' : 'He'} {p.homeLoan === 'yes' ? 'owns her home (with a loan),' : 'is building a financial foundation,'} protects {p.gender === 'female' ? 'her' : 'his'} family with health insurance, and has already begun investing. 
                                </p>
                                <p>
                                    <strong>Your Verdict:</strong> {p.gender === 'female' ? 'Her' : 'His'} overall score is <strong>{score}/100</strong>. You are currently in the <strong>{score > 70 ? 'Optimal' : score > 40 ? 'Growth' : 'Warning'} Zone</strong>. This report provides the exact blueprint to reach 90+.
                                </p>
                            </>
                        )}
                    </div>
                </div>

                <div className="strengths-header">
                    <h2>{isCouple ? 'What Is Already Working (Family Strengths)' : 'What Is Already Working (5 Real Strengths)'}</h2>
                </div>

                <div className="strengths-list">
                    {isCouple ? (
                        coupleStrengthsList.map((s, idx) => (
                            <div key={idx} className="strength-item">
                                <div className="strength-num-circle">{idx + 1}</div>
                                <div className="strength-text"><strong>{s.t}:</strong> {s.d}</div>
                                <div className="strength-icon-right">
                                    {idx === 0 ? <MdSecurity /> :
                                     idx === 1 ? <MdAccountBalanceWallet /> :
                                     idx === 2 ? <MdShield /> :
                                     idx === 3 ? <MdTrendingUp /> :
                                     <MdShowChart />}
                                </div>
                            </div>
                        ))
                    ) : (
                        [
                            { n: 1, t: "Lifestyle discipline", i: <MdSecurity />, d: `spending only ${m.lifestyleRatio?.toFixed(0) || 0}% on lifestyle — better than 85% of Indian professionals ${isCouple ? 'their' : p.gender === 'female' ? 'her' : 'his'} age.` },
                            { n: 2, t: "Family health protection", i: <MdShield />, d: `insuring both ${isCouple ? 'themselves' : p.gender === 'female' ? 'herself' : 'himself'} and ${isCouple ? 'their' : p.gender === 'female' ? 'her' : 'his'} senior citizen parents — a decision that will protect against a single hospitalisation wiping out years of savings.` },
                            { n: 3, t: "Home loan equity building", i: <MdAccountBalanceWallet />, d: `every EMI creates real wealth in property while generating tax deductions.` },
                            { n: 4, t: "Smart 80C strategy", i: <MdTrendingUp />, d: `utilisation of ₹1.5L limit through EPF, PPF and insurance — saving thousands in tax every year.` },
                            { n: 5, t: "Investing habit established", i: <MdShowChart />, d: "SIP and PPF running. The hardest part — starting — is already done." }
                        ].map((s, idx) => (
                            <div key={idx} className="strength-item">
                                <div className="strength-num-circle">{s.n}</div>
                                <div className="strength-text"><strong>{s.t}:</strong> {s.d}</div>
                                <div className="strength-icon-right">{s.i}</div>
                            </div>
                        ))
                    )}
                </div>

                <PageFooter num="02" customText={` www.myyathartha.com | Confidential - for ${name.toUpperCase()} only | Financial awareness & education report.`} />
            </div>


            {/* PAGE 3: THE 5 GAPS (Refined) */}
            <div className="report-page">
                <PageHeader section="1" title="THE 5 CRITICAL GAPS" />
                <div className="page-content-refined" style={{ padding: '20px 0' }}>
                    <div className="gaps-upper-header">
                        <h1 className="gaps-main-title">The Gaps That Need<br/>Your Attention Now</h1>
                    </div>
                    <div className="gaps-vertical-flow">
                        <div className="flow-line"></div>
                        {isCouple ? (() => {
                            const sectionB = jointSalary.sectionB || {};
                            const sectionC = jointSalary.sectionC || {};
                            const sectionD = jointSalary.sectionD || {};
                            const sectionE = jointSalary.sectionE || {};
                            const sectionF = jointSalary.sectionF || {};
                            const sectionG = jointSalary.sectionG || {};

                            const fmtL = (val) => {
                                if (!val || val <= 0) return "₹0.0L";
                                if (val < 100000) return `₹${(val / 1000).toFixed(1)}K`;
                                if (val >= 10000000) return `₹${(val / 10000000).toFixed(2)} Cr`;
                                return `₹${(val / 100000).toFixed(1)}L`;
                            };

                            const fmt = (v) => v ? Math.round(v).toLocaleString('en-IN') : '0';

                            const coupleGaps = [];

                            // 1. Life Insurance Gap (Couple)
                            const lifeActual = (parseFloat(sectionE.husbandLifeCover) || 0) + (parseFloat(sectionE.wifeLifeCover) || 0);
                            const husbandSalary = parseFloat(sectionB.husbandSalary) || 0;
                            const wifeSalary = parseFloat(sectionB.wifeSalary) || 0;
                            const husbandOther = parseFloat(sectionB.husbandOtherIncome) || 0;
                            const wifeOther = parseFloat(sectionB.wifeOtherIncome) || 0;
                            const combinedMonthly = husbandSalary + wifeSalary + husbandOther + wifeOther;
                            const combinedAnnual = combinedMonthly * 12;
                            const lifeTarget = Math.max(25000000, combinedAnnual * 10);
                            const lifeGapRatio = lifeTarget > 0 ? Math.max(0, (lifeTarget - lifeActual) / lifeTarget) : 0;

                            if (lifeGapRatio > 0) {
                                const sev = lifeGapRatio >= 0.8 ? "CRITICAL" : lifeGapRatio >= 0.5 ? "URGENT" : lifeGapRatio >= 0.2 ? "HIGH" : "MEDIUM";
                                const color = sev === "CRITICAL" ? "#dc2626" : sev === "URGENT" ? "#ea580c" : sev === "HIGH" ? "#1e40af" : "#059669";
                                coupleGaps.push({
                                    priority: lifeGapRatio,
                                    title: lifeActual === 0 ? "Zero Life Insurance" : "Life Insurance Gap!",
                                    severity: sev,
                                    color: color,
                                    desc: lifeActual === 0 
                                        ? "Family is completely exposed. Fix this week via Term Insurance." 
                                        : `${fmtL(lifeActual)} (Actual) vs. ${fmtL(lifeTarget)} (Needed). Fix this week via Term Insurance.`,
                                    icon: <MdShield />
                                });
                            }

                            // 2. Emergency Fund Gap (Couple)
                            const emActual = parseFloat(sectionG.emergencyFund) || 0;
                            const jointExpenses = (parseFloat(sectionC.homeLoanEMI) || 0) +
                                                  (parseFloat(sectionC.personalLoanEMI) || 0) +
                                                  (parseFloat(sectionC.carLoanEMI) || 0) +
                                                  (parseFloat(sectionC.groceries) || 0) +
                                                  (parseFloat(sectionC.transport) || 0) +
                                                  (parseFloat(sectionC.dining) || 0) +
                                                  (parseFloat(sectionC.shopping) || 0) +
                                                  (parseFloat(sectionC.healthInsurance) || 0) +
                                                  (parseFloat(sectionC.rent) || 0) +
                                                  (parseFloat(sectionC.schoolFees) || 0) +
                                                  (parseFloat(sectionC.otherExpenses) || 0);
                            const monthlyExpenses = jointExpenses || (combinedMonthly * 0.5) || 50000;
                            const emTarget = monthlyExpenses * 6;
                            const emGapRatio = emTarget > 0 ? Math.max(0, (emTarget - emActual) / emTarget) : 0;

                            if (emGapRatio > 0) {
                                const sev = emGapRatio >= 0.8 ? "CRITICAL" : emGapRatio >= 0.5 ? "URGENT" : emGapRatio >= 0.2 ? "HIGH" : "MEDIUM";
                                const color = sev === "CRITICAL" ? "#dc2626" : sev === "URGENT" ? "#ea580c" : sev === "HIGH" ? "#1e40af" : "#059669";
                                coupleGaps.push({
                                    priority: emGapRatio,
                                    title: emActual === 0 ? "No Emergency Fund" : "Emergency Fund!",
                                    severity: sev,
                                    color: color,
                                    desc: emActual === 0 
                                        ? `₹0 vs ${fmtL(emTarget)} required. One unexpected event can derail the entire plan.`
                                        : `${fmtL(emActual)} (Actual) vs. ${fmtL(emTarget)} (Needed). Automate your emergency fund savings.`,
                                    icon: <MdSecurity />
                                });
                            }

                            // 3. Joint Personal Loan / High Interest Loan (Couple)
                            const plOS = parseFloat(sectionF.personalLoanOutstanding) || 0;
                            const plEMI = parseFloat(sectionF.personalLoanEMI) || parseFloat(sectionC.personalLoanEMI) || 0;
                            const plRate = parseFloat(sectionF.personalLoanRate) || 15;
                            const ccOS = parseFloat(sectionF.creditCardOutstanding) || 0;
                            const ccRate = parseFloat(sectionF.creditCardRate) || 42;
                            const clOS = parseFloat(sectionF.carLoanOutstanding) || 0;
                            const clEMI = parseFloat(sectionF.carLoanEMI) || parseFloat(sectionC.carLoanEMI) || 0;
                            const clRate = parseFloat(sectionF.carLoanRate) || 9;

                            const loans = [];
                            if (plOS > 0) {
                                loans.push({ name: "Joint Personal Loan", os: plOS, emi: plEMI, rate: plRate });
                            }
                            if (ccOS > 0) {
                                loans.push({ name: "Credit Card Debt", os: ccOS, emi: 0, rate: ccRate });
                            }
                            if (clOS > 0) {
                                loans.push({ name: "Car Loan", os: clOS, emi: clEMI, rate: clRate });
                            }
                            const activeLoan = loans.sort((a, b) => b.rate - a.rate)[0];

                            if (activeLoan) {
                                const loanGapRatio = activeLoan.rate >= 30 ? 0.9 : activeLoan.rate >= 14 ? 0.6 : 0.4;
                                const sev = loanGapRatio >= 0.8 ? "CRITICAL" : loanGapRatio >= 0.5 ? "URGENT" : loanGapRatio >= 0.2 ? "HIGH" : "MEDIUM";
                                const color = sev === "CRITICAL" ? "#dc2626" : sev === "URGENT" ? "#ea580c" : sev === "HIGH" ? "#1e40af" : "#059669";
                                coupleGaps.push({
                                    priority: loanGapRatio,
                                    title: `${activeLoan.name} at ${activeLoan.rate}%`,
                                    severity: sev,
                                    color: color,
                                    desc: activeLoan.emi > 0 
                                        ? `Paying \u20b9${fmt(activeLoan.emi)} EMI/month. High-cost loan is a wealth destroyer.`
                                        : `High-cost loan of \u20B9${(activeLoan.os / 100000).toFixed(1)}L outstanding is a wealth destroyer.`,
                                    icon: <MdTimeline />
                                });
                            }

                            // 4. Separate Tax Filing (Couple)
                            const taxSaving = Math.min(85000, Math.round(combinedAnnual * 0.05));
                            if (taxSaving > 0) {
                                coupleGaps.push({
                                    priority: 0.15,
                                    title: "Separate Tax Filing",
                                    severity: "MEDIUM",
                                    color: "#059669",
                                    desc: `Filing individually is losing up to \u20b9${fmt(taxSaving)}/year in tax savings. Split HRA/home loan benefits.`,
                                    icon: <FaFileInvoiceDollar />
                                });
                            }

                            // 5. Surplus Cash (Couple)
                            const investments = (parseFloat(sectionD.husbandEPF) || 0) +
                                                (parseFloat(sectionD.wifeEPF) || 0) +
                                                (parseFloat(sectionD.husbandPPF) || 0) +
                                                (parseFloat(sectionD.wifePPF) || 0) +
                                                (parseFloat(sectionD.husbandMutualFund) || 0) +
                                                (parseFloat(sectionD.wifeMutualFund) || 0) +
                                                (parseFloat(sectionD.husbandELSS) || 0) +
                                                (parseFloat(sectionD.wifeELSS) || 0) +
                                                (parseFloat(sectionD.husbandNPS) || 0) +
                                                (parseFloat(sectionD.wifeNPS) || 0) +
                                                (parseFloat(sectionD.husbandFD) || 0) +
                                                (parseFloat(sectionD.wifeFD) || 0) +
                                                (parseFloat(sectionD.husbandOtherInvest) || 0) +
                                                (parseFloat(sectionD.wifeOtherInvest) || 0);
                            const surplusCash = combinedMonthly - jointExpenses - investments;
                            if (surplusCash > 0) {
                                coupleGaps.push({
                                    priority: 0.05,
                                    title: "Surplus Cash",
                                    severity: "OPTIMISATION",
                                    color: "#7c3aed",
                                    desc: `\u20B9${fmt(surplusCash)}/month unassigned. Needs joint goal allocation.`,
                                    icon: <FaPiggyBank />
                                });
                            }

                            const sortedCoupleGaps = coupleGaps.sort((a, b) => b.priority - a.priority).slice(0, 5);
                            return sortedCoupleGaps.map((gap, index) => (
                                <GapEntry 
                                    key={index}
                                    id={(index + 1).toString()}
                                    title={gap.title}
                                    severity={gap.severity}
                                    color={gap.color}
                                    desc={gap.desc}
                                    icon={gap.icon}
                                />
                            ));
                        })() : (
                            <>
                                {/* 1. Emergency Fund Gap */}
                                {(() => {
                                    const emActual = m.emergencyFund || m.totalLiquid || 0;
                                    const emTarget = (m.totalMonthlyExpenses || (m.totalMonthlyIncome * 0.4) || 30000) * 6;
                                    const emGap = Math.max(0, emTarget - emActual);
                                    const emGapRatio = emTarget > 0 ? emGap / emTarget : 0;
                                    const sev = emGapRatio >= 0.8 ? "CRITICAL" : emGapRatio >= 0.5 ? "URGENT" : emGapRatio >= 0.2 ? "HIGH" : "MODERATE";
                                    const color = sev === "CRITICAL" ? "#dc2626" : sev === "URGENT" ? "#ea580c" : sev === "HIGH" ? "#ca8a04" : "#16a34a";
                                    
                                    return (
                                        <GapEntry 
                                            id="1" title="The Survival Gap" severity={sev} color={color} 
                                            desc={`₹${(emActual / 100000).toFixed(1)}L vs. ₹${(emTarget / 100000).toFixed(1)}L Needed. You need ₹${(emGap / 100000).toFixed(1)}L more for a 6-month buffer.`}
                                            icon={<MdSecurity />}
                                        />
                                    );
                                })()}

                                {/* 2. Life Insurance Gap */}
                                {(() => {
                                    const lifeActual = m.currentLifeCover || 0;
                                    const lifeTarget = (m.totalMonthlyIncome * 12 * 10) || 15000000;
                                    const lifeGap = Math.max(0, lifeTarget - lifeActual);
                                    const lifeGapRatio = lifeTarget > 0 ? lifeGap / lifeTarget : 0;
                                    const sev = lifeGapRatio >= 0.8 ? "CRITICAL" : lifeGapRatio >= 0.5 ? "HIGH" : lifeGapRatio >= 0.2 ? "MODERATE" : "LOW";
                                    const color = sev === "CRITICAL" ? "#dc2626" : sev === "HIGH" ? "#ea580c" : sev === "MODERATE" ? "#ca8a04" : "#16a34a";

                                    return (
                                        <GapEntry 
                                            id="2" title="The Protection Gap" severity={sev} color={color} 
                                            desc={`₹${(lifeActual / 100000).toFixed(1)}L vs. ₹${(lifeTarget / 10000000).toFixed(1)}Cr needed. Your family is exposed.`}
                                            icon={<MdShield />}
                                        />
                                    );
                                })()}

                                {/* 3. Savings Rate Gap */}
                                <GapEntry 
                                    id="3" title="The Savings Gap" severity={m.savingsRate < 20 ? "CRITICAL" : "MODERATE"} 
                                    color={m.savingsRate < 20 ? "#dc2626" : "#ca8a04"} 
                                    desc={`${(m.savingsRate || 0).toFixed(1)}% vs. 25% needed. Idle cash loses 7% to inflation.`}
                                    icon={<MdTrendingUp />}
                                />

                                {/* 4. Medical Gap */}
                                <GapEntry 
                                    id="4" title="The Medical Gap" severity={m.healthInsuranceCover < 500000 ? "CRITICAL" : "MODERATE"} 
                                    color={m.healthInsuranceCover < 500000 ? "#dc2626" : "#ca8a04"} 
                                    desc={m.healthInsuranceCover > 0 ? `₹${(m.healthInsuranceCover / 100000).toFixed(1)}L vs. 10L needed (Base + Top-up).` : "No personal health cover found. Critical risk."}
                                    icon={<MdInfo />}
                                />

                                {/* 5. Debt Gap */}
                                <GapEntry 
                                    id="5" title="The Debt Trap" severity={m.dti > 35 ? "CRITICAL" : "LOW"} 
                                    color={m.dti > 35 ? "#dc2626" : "#16a34a"} 
                                    desc={`DTI: ${m.dti || 0}% vs. 35% benchmark. ${m.dti > 35 ? "High debt stress." : "Debt is well-managed."}`}
                                    icon={<MdTimeline />}
                                />
                            </>
                        )}
                    </div>

                    <div className="big-picture-box">
                        <h3>Summary: <span>The Big Picture</span></h3>
                        <p>Based on your cashflow of ₹{fmt(metrics.totalMonthlyIncome)}, you are saving well but lack the "Safety Shield" of Liquid Cash and Term Insurance. Fix the Gaps 1 & 2 before investing in Gap 4.</p>
                    </div>
                </div>
                <PageFooter num="03" />
            </div>

            {/* PAGE 4: LEGAL & DISCLOSURES (Refined) */}
            <div className="report-page">
                <PageHeader section="0" title="DISCLAIMER & TERMS" />
                <div className="page-content-refined">
                    <div className="disclaimer-text-box">
                        <p><strong>Educational Purpose:</strong> This Financial Diagnosis Report is strictly for educational purposes and provides mathematical analysis based on the inputs provided by you. It does not constitute personal financial, investment, or tax advice.</p>
                        <p><strong>Accuracy of Data:</strong> The results are entirely dependent on the accuracy of the data entered. DigiSahyadri and the Yathartha platform are not liable for any decisions made based on this analysis.</p>
                        <p><strong>No Guarantee:</strong> Past performance of any suggested investment vehicles is not indicative of future results. Please consult with a SEBI-registered Investment Advisor before making financial commitments.</p>
                        <p style={{ fontWeight: 'bold' }}>Confidentiality: This report is private. Do not share your PDF password with unauthorized persons.</p>
                    </div>
                </div>
                <PageFooter num="04" />
            </div>

            {/* PAGE 5: EXECUTIVE VERDICT (Refined) */}
            <div className="report-page">
                <PageHeader section="1" title="EXECUTIVE VERDICT" />
                <div className="page-content-refined">
                    <div className="executive-box">
                        <h3 className="box-title">Dear {p.firstName},</h3>
                        <p className="box-para">Your overall score is <strong>{score}/100</strong>. You have a solid income foundation, but your wealth is currently <strong>"Fragile"</strong> due to protection gaps. Fix these to become <strong>"Antifragile"</strong>.</p>
                    </div>
                    <div className="strength-gap-grid" style={{ marginTop: '40px' }}>
                        <div className="s-column">
                            <h4><MdCheckCircle /> Strengths</h4>
                            <ul>
                                <li><strong>Savings Discipline:</strong> Your {m.savingsRate}% savings rate is above the 30% benchmark.</li>
                                <li><strong>Debt Health:</strong> DTI of {m.dti}% shows excellent repayment capacity.</li>
                            </ul>
                        </div>
                        <div className="g-column">
                            <h4><MdWarning /> Critical Gaps</h4>
                            <ul>
                                <li><strong>Insurance Void:</strong> ₹{fmt(metrics.annualGrossSalary * 10 - metrics.currentLifeCover)} additional cover needed.</li>
                                <li><strong>Buffer Issue:</strong> Emergency fund is only {m.emergencyFundRatio} months (Target: 6).</li>
                            </ul>
                        </div>
                    </div>
                </div>
                <PageFooter num="05" />
            </div>

            {!isSummary && (
                <>
                    {/* PAGE 6: 6-PARAMETER ANALYSIS */}
                    <div className="report-page">
                        <PageHeader section="2" title="6-PARAMETER DIAGNOSIS" />
                        <div className="page-content-refined">
                            <table className="premium-diagnosis-table">
                                <thead>
                                    {isCouple ? (
                                        <tr><th>PARAMETER</th><th>COMBINED NUMBER</th><th>BENCHMARK</th><th>DIAGNOSIS</th><th>PRIORITY</th></tr>
                                    ) : (
                                        <tr><th>PARAMETER</th><th>TARGET</th><th>ACTUAL</th><th>GRADE</th></tr>
                                    )}
                                </thead>
                                <tbody>
                                    {isCouple ? (
                                        <>
                                            {(() => {
                                                const getPColor = (p) => {
                                                    if (p === 'LOW') return '#069668';
                                                    if (p === 'MEDIUM') return '#f59e0b';
                                                    if (p === 'URGENT') return '#f97316';
                                                    if (p === 'CRITICAL') return '#e11d48';
                                                    return '#069668';
                                                };
                                                const coupleItems = [
                                                    {
                                                        icon: <MdAccountBalanceWallet />,
                                                        label: "Combined Savings Rate",
                                                        val: `${srVal.toFixed(1)}% (₹${fmtK(investments)} of ₹${fmtL(combinedMonthly)})`,
                                                        bench: ">20%",
                                                        diag: srVal >= 20 ? 'Excellent' : (srVal >= 10 ? 'Healthy' : '❌ Critical'),
                                                        prio: srVal >= 20 ? 'LOW' : (srVal >= 10 ? 'MEDIUM' : 'URGENT')
                                                    },
                                                    {
                                                        icon: <MdSecurity />,
                                                        label: "Essential Expenses Ratio",
                                                        val: `${eePct.toFixed(0)}% (₹${fmtK(calcEssential)}/₹${fmtL(combinedMonthly)})`,
                                                        bench: "<50%",
                                                        diag: eePct <= 50 ? 'Healthy' : (eePct <= 60 ? 'Manageable' : '❌ Critical'),
                                                        prio: eePct <= 50 ? 'LOW' : (eePct <= 60 ? 'MEDIUM' : 'URGENT')
                                                    },
                                                    {
                                                        icon: <MdShowChart />,
                                                        label: "Lifestyle Spending",
                                                        val: `${lsPct.toFixed(0)}% (₹${fmtK(lifestyleSpending)}/₹${fmtL(combinedMonthly)})`,
                                                        bench: "10-15%",
                                                        diag: lsPct <= 15 ? 'Excellent' : (lsPct <= 20 ? 'Healthy' : '❌ Critical'),
                                                        prio: lsPct <= 15 ? 'LOW' : (lsPct <= 20 ? 'MEDIUM' : 'URGENT')
                                                    },
                                                    {
                                                        icon: <MdTimeline />,
                                                        label: "Joint Debt-to-Income (DTI)",
                                                        val: `${dtiVal.toFixed(1)}% (₹${fmtK(totalEMIs)}/₹${fmtL(combinedMonthly)})`,
                                                        bench: "<35%",
                                                        diag: dtiVal === 0 ? 'Zero Debt' : (dtiVal <= 35 ? 'Manageable' : '❌ Critical'),
                                                        prio: dtiVal === 0 ? 'LOW' : (dtiVal <= 35 ? 'MEDIUM' : 'URGENT')
                                                    },
                                                    {
                                                        icon: <MdSecurity />,
                                                        label: "Joint Emergency Fund",
                                                        val: `₹${fmt(emActual)}`,
                                                        bench: `₹${fmtL(emTarget)}\n(6 months)`,
                                                        diag: efPctFunded >= 100 ? 'Healthy' : (efPctFunded >= 50 ? 'Manageable' : '❌ Critical'),
                                                        prio: efPctFunded >= 100 ? 'LOW' : (efPctFunded >= 50 ? 'MEDIUM' : 'URGENT')
                                                    },
                                                    {
                                                        icon: <MdShield />,
                                                        label: "Joint Life Insurance",
                                                        val: `₹${fmt(lifeActual)}`,
                                                        bench: `₹${fmtL(lifeTarget)}`,
                                                        diag: (lifeActual / (lifeTarget || 1)) >= 1 ? 'Healthy' : ((lifeActual / (lifeTarget || 1)) >= 0.5 ? 'Inadequate' : '❌ Critical'),
                                                        prio: (lifeActual / (lifeTarget || 1)) >= 1 ? 'LOW' : ((lifeActual / (lifeTarget || 1)) >= 0.5 ? 'MEDIUM' : 'CRITICAL')
                                                    }
                                                ];
                                                return coupleItems.map((item, idx) => {
                                                    const pColor = getPColor(item.prio);
                                                    return (
                                                        <tr key={idx}>
                                                            <td>
                                                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                                    {item.icon} {item.label}
                                                                </div>
                                                            </td>
                                                            <td>
                                                                <div style={{ fontWeight: 'bold' }}>{item.val}</div>
                                                            </td>
                                                            <td style={{ whiteSpace: 'pre-line' }}>{item.bench}</td>
                                                            <td>
                                                                <span style={{
                                                                    border: `1px solid ${pColor}`,
                                                                    borderRadius: '6px',
                                                                    padding: '4px 8px',
                                                                    color: pColor,
                                                                    fontWeight: 'bold',
                                                                    fontSize: '12px',
                                                                    display: 'inline-block',
                                                                    minWidth: '70px',
                                                                    textAlign: 'center'
                                                                }}>
                                                                    {item.diag}
                                                                </span>
                                                            </td>
                                                            <td>
                                                                <span style={{
                                                                    background: pColor,
                                                                    color: 'white',
                                                                    borderRadius: '6px',
                                                                    padding: '4px 8px',
                                                                    fontWeight: 'bold',
                                                                    fontSize: '12px',
                                                                    display: 'inline-block',
                                                                    minWidth: '80px',
                                                                    textAlign: 'center'
                                                                }}>
                                                                    {item.prio}
                                                                </span>
                                                            </td>
                                                        </tr>
                                                    );
                                                });
                                            })()}
                                        </>
                                    ) : (
                                        <>
                                            <tr><td><MdSecurity /> Emergency Fund</td><td>&gt; 6 Mo</td><td>{m.emergencyFundRatio} Mo</td><td><GradeBadge grade={grades.emergencyFund} /></td></tr>
                                            <tr><td><MdShield /> Life Insurance</td><td>{calculations.diagnosis.ica.benchmark}</td><td><div style={{ fontWeight: 'bold' }}>{calculations.diagnosis.ica.value}</div><div style={{ fontSize: '10px', color: '#64748b' }}>(₹{metrics.currentLifeCover?.toLocaleString()} Cover)</div></td><td><GradeBadge grade={grades.insurance} /></td></tr>
                                            <tr><td><MdAccountBalanceWallet /> Savings Rate</td><td>&gt; 30%</td><td>{m.savingsRate}%</td><td><GradeBadge grade={grades.savings} /></td></tr>
                                            <tr><td><MdTimeline /> Debt-to-Income</td><td>&lt; 35%</td><td>{m.dti}%</td><td><GradeBadge grade={grades.debt} /></td></tr>
                                            <tr><td><MdShowChart /> Lifestyle Rate</td><td>&lt; 20%</td><td>{m.lifestyleRate}%</td><td><GradeBadge grade={grades.lifestyle} /></td></tr>
                                            <tr><td><MdInfo /> Medical Cover</td><td>10L Base</td><td>{metrics.healthInsuranceCover > 0 ? `₹${(metrics.healthInsuranceCover / 100000).toFixed(1)}L` : 'No'}</td><td><GradeBadge grade={metrics.healthInsuranceCover >= 500000 ? 'A' : (metrics.healthInsuranceCover > 0 ? 'B' : 'D')} /></td></tr>
                                        </>
                                    )}
                                </tbody>
                            </table>
                        </div>
                        <PageFooter num="06" />
                    </div>

                    {/* PAGE 7: MONTHLY DATA SNAPSHOT */}
                    <div className="report-page">
                        <PageHeader section="3" title="MONTHLY DATA SNAPSHOT" />
                        <div className="page-content-refined">
                            {isCouple ? (() => {
                                const sectionA = jointSalary.sectionA || {};
                                const sectionB = jointSalary.sectionB || {};
                                const sectionC = jointSalary.sectionC || {};
                                const sectionD = jointSalary.sectionD || {};
                                const sectionE = jointSalary.sectionE || {};
                                const sectionF = jointSalary.sectionF || {};

                                const safeParse = (v) => { 
                                    if (typeof v === "number") return v; 
                                    if (!v) return 0; 
                                    return parseFloat(v.toString().replace(/[₹, \s]/g, "")) || 0; 
                                };

                                const capitalize = (str) => {
                                    if (!str) return "";
                                    return str.trim().split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
                                };

                                const hName = capitalize((sectionA.husbandName || "Husband").split(' ')[0]);
                                const wName = capitalize((sectionA.wifeName || "Wife").split(' ')[0]);

                                // Incomes
                                const hSalary = safeParse(sectionB.husbandSalary) || 0;
                                const wSalary = safeParse(sectionB.wifeSalary) || 0;
                                const hOther = safeParse(sectionB.husbandOtherIncome) || 0;
                                const wOther = safeParse(sectionB.wifeOtherIncome) || 0;

                                const totalSalary = hSalary + wSalary;
                                const totalOther = hOther + wOther;
                                const hTotal = hSalary + hOther;
                                const wTotal = wSalary + wOther;
                                const combinedMonthly = hTotal + wTotal;

                                const salaryPct = combinedMonthly > 0 ? Math.round((totalSalary / combinedMonthly) * 100) : 0;
                                const otherPct = combinedMonthly > 0 ? Math.round((totalOther / combinedMonthly) * 100) : 0;

                                // Categories Calculations
                                const hl = safeParse(sectionF.homeLoanEMI) || safeParse(sectionC.homeLoanEMI) || 0;
                                const pl = safeParse(sectionF.personalLoanEMI) || safeParse(sectionC.personalLoanEMI) || 0;
                                const plRate = safeParse(sectionF.personalLoanRate) || 15;
                                const groceriesUtilities = (safeParse(sectionC.groceries) || 0) + (safeParse(sectionC.rent) || 0) + (safeParse(sectionC.schoolFees) || 0) + (safeParse(sectionC.otherExpenses) || 0);
                                const transport = (safeParse(sectionC.transport) || 0) + (safeParse(sectionF.carLoanEMI) || safeParse(sectionC.carLoanEMI) || 0);
                                const dining = safeParse(sectionC.dining) || 0;
                                const shopping = safeParse(sectionC.shopping) || 0;
                                const insurance = (safeParse(sectionC.healthInsurance) || 0) + (safeParse(sectionE.husbandLifePremium) || 0) + (safeParse(sectionE.wifeLifePremium) || 0) + (safeParse(sectionE.husbandHealthPremium) || 0) + (safeParse(sectionE.wifeHealthPremium) || 0);
                                const investments = (safeParse(sectionD.husbandEPF) || 0) + (safeParse(sectionD.wifeEPF) || 0) +
                                                    (safeParse(sectionD.husbandPPF) || 0) + (safeParse(sectionD.wifePPF) || 0) +
                                                    (safeParse(sectionD.husbandMutualFund) || 0) + (safeParse(sectionD.wifeMutualFund) || 0) +
                                                    (safeParse(sectionD.husbandELSS) || 0) + (safeParse(sectionD.wifeELSS) || 0) +
                                                    (safeParse(sectionD.husbandNPS) || 0) + (safeParse(sectionD.wifeNPS) || 0) +
                                                    (safeParse(sectionD.husbandFD) || 0) + (safeParse(sectionD.wifeFD) || 0) +
                                                    (safeParse(sectionD.husbandOtherInvest) || 0) + (safeParse(sectionD.wifeOtherInvest) || 0);

                                const totalTracked = hl + pl + groceriesUtilities + transport + dining + shopping + insurance + investments;
                                const unassignedSurplus = Math.max(0, combinedMonthly - totalTracked);

                                const lifeCover = (safeParse(sectionE.husbandLifeCover) || 0) + (safeParse(sectionE.wifeLifeCover) || 0);
                                const invPct = combinedMonthly > 0 ? (investments / combinedMonthly) : 0;

                                const table2Rows = [
                                    { label: "Joint Home Loan EMI", amt: hl, hc: hl > 0 ? "Productive" : "Healthy", hcClass: "healthy", notes: hl > 0 ? "Good debt" : "No home loan EMI" },
                                    { label: "Joint Personal Loan EMI", amt: pl, hc: pl > 0 ? "Expensive" : "Zero Debt", hcClass: pl > 0 ? "expensive" : "healthy", notes: pl > 0 ? `${plRate}% interest` : "No high-interest debt." },
                                    { label: "Groceries & Utilities", amt: groceriesUtilities, hc: "Healthy", hcClass: "healthy", notes: "Combined" },
                                    { label: "Transport", amt: transport, hc: "Manageable", hcClass: "healthy", notes: "Fuel + maintenance" },
                                    { label: "Dining & Entertainment", amt: dining, hc: "Healthy", hcClass: "healthy", notes: "No cuts needed" },
                                    { label: "Shopping", amt: shopping, hc: "Manageable", hcClass: "healthy", notes: "Combined" },
                                    { label: "Insurance Premiums", amt: insurance, hc: lifeCover === 0 ? "Inadequate" : "Smart", hcClass: lifeCover === 0 ? "expensive" : "healthy", notes: lifeCover === 0 ? "Health only, no life" : "Adequate protection" },
                                    { label: "Investments (SIP + EPF)", amt: investments, hc: invPct < 0.2 ? "Too Low" : "Healthy", hcClass: invPct < 0.2 ? "expensive" : "healthy", notes: invPct < 0.2 ? "Needs 3x increase" : "Good retirement base" }
                                ];

                                return (
                                    <div className="couple-snapshot-container">
                                        <h4 className="couple-section-title">Combined Monthly Family Income</h4>
                                        <table className="couple-table">
                                            <thead>
                                                <tr>
                                                    <th>Source</th>
                                                    <th>{hName}</th>
                                                    <th>{wName}</th>
                                                    <th>Total</th>
                                                    <th>% of Combined</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                <tr>
                                                    <td>Salary (Post-Tax)</td>
                                                    <td>₹{fmt(hSalary)}</td>
                                                    <td>₹{fmt(wSalary)}</td>
                                                    <td>₹{fmt(totalSalary)}</td>
                                                    <td>{salaryPct}%</td>
                                                </tr>
                                                <tr>
                                                    <td>Other Income (Freelance/Rent)</td>
                                                    <td>₹{fmt(hOther)}</td>
                                                    <td>₹{fmt(wOther)}</td>
                                                    <td>₹{fmt(totalOther)}</td>
                                                    <td>{otherPct}%</td>
                                                </tr>
                                                <tr className="total-row">
                                                    <td>TOTAL FAMILY INCOME</td>
                                                    <td>₹{fmt(hTotal)}</td>
                                                    <td>₹{fmt(wTotal)}</td>
                                                    <td>₹{fmt(combinedMonthly)}</td>
                                                    <td>100%</td>
                                                </tr>
                                            </tbody>
                                        </table>

                                        <h4 className="couple-section-title" style={{ marginTop: '10px' }}>Tracked Expenses & Investments</h4>
                                        <table className="couple-table">
                                            <thead>
                                                <tr>
                                                    <th>Category</th>
                                                    <th>Amount</th>
                                                    <th>% of Income</th>
                                                    <th>Health Check</th>
                                                    <th>Notes</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {table2Rows.map((row, idx) => {
                                                    const pctVal = combinedMonthly > 0 ? Math.round((row.amt / combinedMonthly) * 100) : 0;
                                                    return (
                                                        <tr key={idx}>
                                                            <td>{row.label}</td>
                                                            <td>₹{fmt(row.amt)}</td>
                                                            <td>{pctVal}%</td>
                                                            <td>
                                                                <span className={`couple-badge ${row.hcClass}`}>
                                                                    {row.hc}
                                                                </span>
                                                            </td>
                                                            <td>{row.notes}</td>
                                                        </tr>
                                                    );
                                                })}
                                                <tr className="total-row">
                                                    <td>TOTAL TRACKED</td>
                                                    <td>₹{fmt(totalTracked)}</td>
                                                    <td>{combinedMonthly > 0 ? Math.round((totalTracked / combinedMonthly) * 100) : 0}%</td>
                                                    <td></td>
                                                    <td></td>
                                                </tr>
                                                <tr className="surplus-row">
                                                    <td>UNASSIGNED SURPLUS</td>
                                                    <td>₹{fmt(unassignedSurplus)}</td>
                                                    <td>{combinedMonthly > 0 ? Math.round((unassignedSurplus / combinedMonthly) * 100) : 0}%</td>
                                                    <td>
                                                        <span className="couple-badge opportunity">
                                                            Opportunity
                                                        </span>
                                                    </td>
                                                    <td>Needs joint plan</td>
                                                </tr>
                                            </tbody>
                                        </table>

                                        <div className="couple-insight-box">
                                            <strong>&gt; Key Insight:</strong>
                                            <span>You have ₹{fmt(unassignedSurplus)} of unassigned surplus every month. This report redirects it to build wealth, not waste it.</span>
                                        </div>
                                    </div>
                                );
                            })() : (
                                <table className="premium-diagnosis-table" style={{ background: '#f8fafc' }}>
                                    <thead><tr><th>Cashflow Category</th><th>Amount</th><th>% of Inc</th></tr></thead>
                                    <tbody>
                                        <tr><td>Total In-Flow (Gross)</td><td>₹{fmt(metrics.totalMonthlyIncome)}</td><td>100%</td></tr>
                                        <tr><td>Essential Needs (Rent, Food, EMI)</td><td>₹{fmt(metrics.totalEssentialExpenses - (metrics.lifeInsurancePremium || 0) - (metrics.healthInsurancePremium || 0))}</td><td>{((metrics.totalEssentialExpenses - metrics.lifeInsurancePremium - metrics.healthInsurancePremium) / metrics.totalMonthlyIncome * 100).toFixed(1)}%</td></tr>
                                        <tr><td>Life Insurance Premium</td><td>₹{fmt(metrics.lifeInsurancePremium)}</td><td>{(metrics.lifeInsurancePremium / metrics.totalMonthlyIncome * 100).toFixed(1)}%</td></tr>
                                        <tr><td>Health Insurance Premium</td><td>₹{fmt(metrics.healthInsurancePremium)}</td><td>{(metrics.healthInsurancePremium / metrics.totalMonthlyIncome * 100).toFixed(1)}%</td></tr>
                                        <tr><td>Impulse & Lifestyle</td><td>₹{fmt(metrics.lifestyleExpenses || metrics.totalLifestyleExpenses)}</td><td>{m.lifestyleRate || 0}%</td></tr>
                                        <tr><td>Debt Servicing (EMIs)</td><td>₹{fmt(metrics.totalEMI)}</td><td>{m.dti}%</td></tr>
                                        <tr style={{ background: '#1a7a5e', color: 'white' }}><td>Investable Surplus</td><td>₹{fmt(metrics.monthlySurplus)}</td><td>{m.savingsRate}%</td></tr>
                                    </tbody>
                                </table>
                            )}
                        </div>
                        <PageFooter num="07" />
                    </div>

                    {/* PAGE 8: EMERGENCY FUND MATH */}
                    <div className="report-page">
                        <PageHeader section="3" title="EMERGENCY FUND MATH" />
                        <div className="page-content-refined">
                            <div style={{ textAlign: 'center', margin: '40px 0' }}>
                                <div className="circular-progress-box">
                                    <div className="progress-outer">
                                        <div className="progress-value">{Math.round((metrics.emergencyFund / (metrics.totalMonthlyExpenses * 6)) * 100)}%</div>
                                        <div className="progress-label">FUNDED</div>
                                    </div>
                                </div>
                                <div className="math-row" style={{ marginTop: '50px' }}>
                                    {(() => {
                                        const target = (metrics.totalMonthlyExpenses || (metrics.totalMonthlyIncome * 0.4) || 30000) * 6;
                                        const actual = metrics.emergencyFund || 0;
                                        const gap = Math.max(0, target - actual);
                                        return (
                                            <>
                                                <div className="math-box safe"><label>SECURITY DEPOSIT (TARGET)</label><strong>₹{fmt(target)}</strong></div>
                                                <div className="math-box alert"><label>CURRENT LIQUIDITY</label><strong>₹{fmt(actual)}</strong></div>
                                                {gap > 0 && <div className="math-box needed"><label>NEEDED AMOUNT (GAP)</label><strong>₹{fmt(gap)}</strong></div>}
                                            </>
                                        );
                                    })()}
                                </div>
                            </div>
                        </div>
                        <PageFooter num="08" />
                    </div>

                    {/* PAGE 9: EDUCATION: LIQUIDITY */}
                    <div className="report-page">
                        <PageHeader section="3" title="THE SURVIVAL BUFFER" />
                        <div className="page-content-refined">
                            <h3 style={{ color: '#1a7a5e', marginBottom: '20px' }}>Why Liquidity Trumps Returns?</h3>
                            <p style={{ lineHeight: 1.8 }}>Many Indians invest their first ₹1 Lakh in Equity. That is a mistake. Without a buffer, market volatility can force you to sell your investments at a loss for a medical emergency. <strong>Liquidity is not for profit; it is for peace.</strong></p>
                        </div>
                        <PageFooter num="09" />
                    </div>

                    {/* PAGE 10: LIFE INSURANCE DEPTH */}
                    <div className="report-page">
                        <PageHeader section="4" title="LIFE INSURANCE ANALYSIS" />
                        <div className="page-content-refined">
                            <HighlightBox color="#ef4444">
                                <p style={{ margin: 0, fontWeight: 700 }}>Your Gap: ₹{fmt(metrics.annualGrossSalary * 10 - metrics.currentLifeCover)}. You are currently {Math.round(100 - (metrics.currentLifeCover / (metrics.annualGrossSalary * 10) * 100))}% underinsured.</p>
                            </HighlightBox>
                            
                            <h3 style={{ color: '#1a7a5e', marginTop: '20px', marginBottom: '15px' }}>Dynamic Insurance Gap Calculation</h3>
                            <table className="premium-diagnosis-table" style={{ fontSize: '13px' }}>
                                <thead>
                                    <tr style={{ background: '#f1f5f9' }}>
                                        <th style={{ color: '#334155' }}>Element</th>
                                        <th style={{ color: '#334155' }}>Details</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr><td><strong>Formula</strong></td><td>(Life Insurance Cover ÷ Annual Income)</td></tr>
                                    <tr><td><strong>Your Calculation</strong></td><td>{calculations.diagnosis.ica.calculation}</td></tr>
                                    <tr><td><strong>Target</strong></td><td>10–15× annual income</td></tr>
                                    <tr><td><strong>Grading Scale</strong></td><td>A: &gt;15×, B: 10–15×, C: 5–10×, D: 1–5×, F: &lt;1×</td></tr>
                                    <tr style={{ background: '#f8fafc' }}><td><strong>Your Grade</strong></td><td><GradeBadge grade={grades.insurance} /> ({calculations.diagnosis.ica.status})</td></tr>
                                </tbody>
                            </table>

                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '30px', marginTop: '30px' }}>
                                <div className="ins-box bad"><h4>Money Back / ULIPs / Endowment</h4><p>Avg. Returns: 4.5%<br/>Verdict: 🚨 Avoid for Wealth</p></div>
                                <div className="ins-box good"><h4>Pure Term Plan</h4><p>Target: 10x-15x Protection<br/>Verdict: ✅ Priority Buy</p></div>
                            </div>
                        </div>
                        <PageFooter num="10" />
                    </div>

                    {/* PAGE 11: MEDICAL (10L RULE) */}
                    <div className="report-page">
                        <PageHeader section="4" title="THE 10L MEDICAL RULE" />
                        <div className="page-content-refined">
                            <p style={{ fontSize: '18px', lineHeight: 1.8 }}>You need a ₹5 Lakh Base + ₹15 Lakh Super Top-up. This "Hybrid Model" saves you 60% in premium while giving 4x the cover of any corporate policy.</p>
                        </div>
                        <PageFooter num="11" />
                    </div>

                    {/* PAGE 12: DEBT ANALYSIS */}
                    <div className="report-page">
                        <PageHeader section="5" title="DEBT & EMI DYNAMICS" />
                        <div className="page-content-refined">
                            <div className="debt-meter-box">
                                <h4 style={{ color: m.dti > 35 ? '#c0392b' : '#1a7a5e' }}>EMI STATUS: {m.dti > 35 ? 'DANGEROUS' : 'HEALTHY'}</h4>
                                <p>Debt to Income: {m.dti}%</p>
                            </div>
                        </div>
                        <PageFooter num="12" />
                    </div>

                    {/* PAGE 13: THE TAX WATERFALL */}
                    <div className="report-page">
                        <PageHeader section="6" title="TAX OPTIMIZATION" />
                        <div className="page-content-refined">
                            <div className="tax-visual-container">
                                <div className="visual-bar gross"><div className="label">GROSS INCOME</div></div>
                                <div className="waterfall-arrow">⬇</div>
                                <div className="visual-bar taxes" style={{ height: '20%' }}><div className="label">TAX SLABS</div></div>
                            </div>
                            <HighlightBox><p>Potential Savings: ₹78,000/yr through 80C + NPS + 80D optimization.</p></HighlightBox>
                        </div>
                        <PageFooter num="13" />
                    </div>

                    {/* PAGE 14: MASTER ACTION CHECKLIST */}
                    <div className="report-page">
                        <PageHeader section="10" title={isCouple ? "MASTER JOINT ACTION CHECKLIST" : "MASTER ACTION CHECKLIST"} />
                        <div className="page-content-refined" style={{ padding: '20px 0' }}>
                            <p style={{ fontSize: '13.5px', color: '#475569', lineHeight: '1.6', marginBottom: '25px', textAlign: 'justify' }}>
                                This is where your financial story officially begins. Every tick you make here is a real, irreversible step toward the life you are building. Print it. Put it where you can see it. Come back to it in 6 months and feel the quiet pride of someone who actually did what most people only talk about.
                            </p>
                            {isCouple ? (
                                <div className="checklist-container">
                                    <table className="checklist-table">
                                        <thead>
                                            <tr>
                                                <th style={{ width: '40px', textAlign: 'center' }}>
                                                    <span className="checklist-checkbox" style={{ display: 'inline-block' }}></span>
                                                </th>
                                                <th><span className="checklist-badge">Action</span></th>
                                                <th><span className="checklist-badge">By When</span></th>
                                                <th><span className="checklist-badge">Responsible</span></th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {(() => {
                                                const sA = jointSalary.sectionA || {};
                                                const sB = jointSalary.sectionB || {};
                                                const sC = jointSalary.sectionC || {};
                                                const sD = jointSalary.sectionD || {};
                                                const sE = jointSalary.sectionE || {};
                                                const sF = jointSalary.sectionF || {};
                                                const sG = jointSalary.sectionG || {};
                                                const sH = jointSalary.sectionH || {};

                                                const fmt = (v) => Math.round(v || 0).toLocaleString('en-IN');

                                                const formatCover = (val) => {
                                                    if (val >= 10000000) return `${(val / 10000000).toFixed(1).replace(/\.0$/, '')}Cr`;
                                                    return `${Math.round(val / 100000)}L`;
                                                };

                                                const hN = (sA.husbandName || "Rohan").trim().split(' ')[0];
                                                const wN = (sA.wifeName || "Priya").trim().split(' ')[0];
                                                const hFirst = hN.charAt(0).toUpperCase() + hN.slice(1);
                                                const wFirst = wN.charAt(0).toUpperCase() + wN.slice(1);

                                                const hSal = safeParse(sB.husbandSalary) || 0;
                                                const wSal = safeParse(sB.wifeSalary) || 0;
                                                const hOther = safeParse(sB.husbandOtherIncome) || 0;
                                                const wOther = safeParse(sB.wifeOtherIncome) || 0;
                                                const hTotalInc = hSal + hOther;
                                                const wTotalInc = wSal + wOther;
                                                const combinedMonthly = hSal + wSal + hOther + wOther;

                                                const hCov = hTotalInc * 12 * 10;
                                                const wCov = wTotalInc * 12 * 10;
                                                const hTargCover = hCov > 0 ? Math.round(hCov / 2500000) * 2500000 : 0;
                                                const wTargCover = wCov > 0 ? Math.round(wCov / 2500000) * 2500000 : 0;
                                                const hTarg = formatCover(Math.max(10000000, hTargCover));
                                                const wTarg = formatCover(Math.max(10000000, wTargCover));

                                                const husbandLifeCover = safeParse(sE.husbandLifeCover) || 0;
                                                const wifeLifeCover = safeParse(sE.wifeLifeCover) || 0;

                                                let hTermAction = "";
                                                let hTermWhen = "";
                                                let hTermWho = "";
                                                if (hTotalInc === 0 && husbandLifeCover === 0) {
                                                    hTermAction = `No life cover needed for ${hFirst} (no active income)`;
                                                    hTermWhen = "N/A";
                                                    hTermWho = "N/A";
                                                } else if (husbandLifeCover >= Math.max(10000000, hTargCover)) {
                                                    hTermAction = `Maintain ${hFirst}'s term insurance of ₹${formatCover(husbandLifeCover)}`;
                                                    hTermWhen = "Ongoing";
                                                    hTermWho = hFirst;
                                                } else {
                                                    hTermAction = husbandLifeCover > 0
                                                        ? `Increase ${hFirst}'s term cover by ₹${formatCover(Math.max(10000000, hTargCover) - husbandLifeCover)} (target ₹${hTarg})`
                                                        : `${hFirst} buys ₹${hTarg} term plan`;
                                                    hTermWhen = "This week";
                                                    hTermWho = hFirst;
                                                }

                                                let wTermAction = "";
                                                let wTermWhen = "";
                                                let wTermWho = "";
                                                if (wTotalInc === 0 && wifeLifeCover === 0) {
                                                    wTermAction = `No life cover needed for ${wFirst} (no active income)`;
                                                    wTermWhen = "N/A";
                                                    wTermWho = "N/A";
                                                } else if (wifeLifeCover >= Math.max(10000000, wTargCover)) {
                                                    wTermAction = `Maintain ${wFirst}'s term insurance of ₹${formatCover(wifeLifeCover)}`;
                                                    wTermWhen = "Ongoing";
                                                    wTermWho = wFirst;
                                                } else {
                                                    wTermAction = wifeLifeCover > 0
                                                        ? `Increase ${wFirst}'s term cover by ₹${formatCover(Math.max(10000000, wTargCover) - wifeLifeCover)} (target ₹${wTarg})`
                                                        : `${wFirst} buys ₹${wTarg} term plan`;
                                                    wTermWhen = "This week";
                                                    wTermWho = wFirst;
                                                }

                                                const emAct = safeParse(sG.emergencyFund) || 0;
                                                const hLoan = safeParse(sF.homeLoanEMI) || safeParse(sC.homeLoanEMI) || 0;
                                                const pLoan = safeParse(sF.personalLoanEMI) || safeParse(sC.personalLoanEMI) || 0;
                                                const cLoan = safeParse(sF.carLoanEMI) || safeParse(sC.carLoanEMI) || 0;
                                                const groc = (safeParse(sC.groceries) || 0) + (safeParse(sC.rent) || 0) + (safeParse(sC.schoolFees) || 0) + (safeParse(sC.otherExpenses) || 0);
                                                const dine = safeParse(sC.dining) || 0;
                                                const shop = safeParse(sC.shopping) || 0;
                                                const ins = (safeParse(sC.healthInsurance) || 0) + (safeParse(sE.husbandLifePremium) || 0) + (safeParse(sE.wifeLifePremium) || 0) + (safeParse(sE.husbandHealthPremium) || 0) + (safeParse(sE.wifeHealthPremium) || 0);

                                                const jExpenses = groc + (safeParse(sC.transport) || 0) + dine + shop + ins + hLoan + pLoan + cLoan;
                                                const mExpenses = jExpenses || (combinedMonthly * 0.5) || 0;
                                                const tEF = mExpenses * 6;
                                                const eGap = Math.max(0, tEF - emAct);
                                                const efTrans = eGap > 0 ? Math.max(1000, Math.round(eGap / 18 / 1000) * 1000) : 0;

                                                let efAction = "";
                                                let efWhen = "";
                                                let efWho = "";
                                                if (combinedMonthly === 0) {
                                                    efAction = "No emergency fund target (No active income)";
                                                    efWhen = "N/A";
                                                    efWho = "N/A";
                                                } else if (eGap === 0) {
                                                    efAction = `Maintain joint liquid fund buffer of ₹${formatCover(tEF)}`;
                                                    efWhen = "Ongoing";
                                                    efWho = (hTotalInc > 0 && wTotalInc > 0) ? "Joint" : (hTotalInc > 0 ? hFirst : wFirst);
                                                } else {
                                                    efAction = `Open joint liquid fund, start ₹${fmtK(efTrans)}/month auto-debit`;
                                                    efWhen = "This week";
                                                    efWho = (hTotalInc > 0 && wTotalInc > 0) ? "Joint" : (hTotalInc > 0 ? hFirst : wFirst);
                                                }

                                                const taxReg = sH.taxRegime || "old";
                                                let taxAction = "";
                                                let taxWhen = "";
                                                let taxWho = "";
                                                if (combinedMonthly === 0) {
                                                    taxAction = "No tax regime declaration needed (No active income)";
                                                    taxWhen = "N/A";
                                                    taxWho = "N/A";
                                                } else {
                                                    const hasSalH = hSal > 0;
                                                    const hasSalW = wSal > 0;
                                                    if (hasSalH && hasSalW) {
                                                        taxAction = `Declare ${taxReg.toUpperCase()} Tax Regime to both HRs`;
                                                        taxWhen = "This week";
                                                        taxWho = "Both";
                                                    } else if (hasSalH) {
                                                        taxAction = `Declare ${taxReg.toUpperCase()} Tax Regime to HR for ${hFirst}`;
                                                        taxWhen = "This week";
                                                        taxWho = hFirst;
                                                    } else if (hasSalW) {
                                                        taxAction = `Declare ${taxReg.toUpperCase()} Tax Regime to HR for ${wFirst}`;
                                                        taxWhen = "This week";
                                                        taxWho = wFirst;
                                                    } else {
                                                        taxAction = `Adopt ${taxReg.toUpperCase()} Tax Regime for tax filing`;
                                                        taxWhen = "Filing deadline";
                                                        taxWho = "Both";
                                                    }
                                                }

                                                const rentVal = safeParse(sC.rent) || 0;
                                                let hrAction = "";
                                                let hrWhen = "";
                                                let hrWho = "";
                                                if (combinedMonthly === 0) {
                                                    hrAction = "No HR submission required (No active income)";
                                                    hrWhen = "N/A";
                                                    hrWho = "N/A";
                                                } else {
                                                    const hasSalH = hSal > 0;
                                                    const hasSalW = wSal > 0;
                                                    if (rentVal > 0) {
                                                        if (sH.claimHRA === 'husband' && hasSalH) {
                                                            hrAction = `Submit rent agreement + HRA declaration for ${hFirst}`;
                                                            hrWhen = "This week";
                                                            hrWho = hFirst;
                                                        } else if (sH.claimHRA === 'wife' && hasSalW) {
                                                            hrAction = `Submit rent agreement + HRA declaration for ${wFirst}`;
                                                            hrWhen = "This week";
                                                            hrWho = wFirst;
                                                        } else {
                                                            hrAction = "Submit joint rent agreement + HRA declaration";
                                                            hrWhen = "This week";
                                                            hrWho = (hasSalH && hasSalW) ? "Both" : (hasSalH ? hFirst : wFirst);
                                                        }
                                                    } else if (hLoan > 0) {
                                                        hrAction = "Submit home loan interest certificate to HR";
                                                        hrWhen = "This week";
                                                        hrWho = (hasSalH && hasSalW) ? "Both" : (hasSalH ? hFirst : wFirst);
                                                    } else {
                                                        hrAction = "Submit PPF/ELSS investment declarations to HR";
                                                        hrWhen = "This week";
                                                        hrWho = (hasSalH && hasSalW) ? "Both" : (hasSalH ? hFirst : wFirst);
                                                    }
                                                }

                                                const hPPF = safeParse(sD.husbandPPF) || 0;
                                                const wPPF = safeParse(sD.wifePPF) || 0;
                                                let ppfAction = "";
                                                let ppfWhen = "";
                                                let ppfWho = "";
                                                if (combinedMonthly === 0) {
                                                    ppfAction = "No PPF contribution recommended (No active income)";
                                                    ppfWhen = "N/A";
                                                    ppfWho = "N/A";
                                                } else {
                                                    if (hTotalInc > 0 && wTotalInc > 0) {
                                                        if (hPPF >= 12500 && wPPF >= 12500) {
                                                            ppfAction = `Maintain PPF contribution of ₹12.5k/month each`;
                                                            ppfWhen = "Ongoing";
                                                            ppfWho = "Both";
                                                        } else {
                                                            const parts = [];
                                                            if (hPPF < 12500) parts.push(`${hFirst}: ₹${fmtK(hPPF)}→₹12.5k`);
                                                            if (wPPF < 12500) parts.push(`${wFirst}: ₹${fmtK(wPPF)}→₹12.5k`);
                                                            ppfAction = `Increase PPF to ₹12.5k/month (${parts.join(', ')})`;
                                                            ppfWhen = "This month";
                                                            ppfWho = "Both";
                                                        }
                                                    } else if (hTotalInc > 0) {
                                                        if (hPPF >= 12500) {
                                                            ppfAction = `Maintain PPF contribution of ₹12.5k/month for ${hFirst}`;
                                                            ppfWhen = "Ongoing";
                                                            ppfWho = hFirst;
                                                        } else {
                                                            ppfAction = `Increase PPF to ₹12.5k/month for ${hFirst} (current: ₹${fmtK(hPPF)})`;
                                                            ppfWhen = "This month";
                                                            ppfWho = hFirst;
                                                        }
                                                    } else {
                                                        if (wPPF >= 12500) {
                                                            ppfAction = `Maintain PPF contribution of ₹12.5k/month for ${wFirst}`;
                                                            ppfWhen = "Ongoing";
                                                            ppfWho = wFirst;
                                                        } else {
                                                            ppfAction = `Increase PPF to ₹12.5k/month for ${wFirst} (current: ₹${fmtK(wPPF)})`;
                                                            ppfWhen = "This month";
                                                            ppfWho = wFirst;
                                                        }
                                                    }
                                                }

                                                const hELSS = safeParse(sD.husbandELSS) || 0;
                                                const wELSS = safeParse(sD.wifeELSS) || 0;
                                                const hMF = safeParse(sD.husbandMutualFund) || 0;
                                                const wMF = safeParse(sD.wifeMutualFund) || 0;
                                                const currELSS = hELSS + wELSS + hMF + wMF;
                                                const targetELSS = combinedMonthly > 0 ? Math.max(10000, Math.round(combinedMonthly * 0.1 / 1000) * 1000) : 0;

                                                let elssAction = "";
                                                let elssWhen = "";
                                                let elssWho = "";
                                                if (combinedMonthly === 0) {
                                                    elssAction = "No ELSS SIP recommended (No active income)";
                                                    elssWhen = "N/A";
                                                    elssWho = "N/A";
                                                } else {
                                                    const whoRole = (hTotalInc > 0 && wTotalInc > 0) ? "Joint" : (hTotalInc > 0 ? hFirst : wFirst);
                                                    elssWho = whoRole;
                                                    const prefix = whoRole === "Joint" ? "joint " : "";
                                                    if (currELSS >= targetELSS) {
                                                        elssAction = `Maintain ${prefix}ELSS SIP of ₹${fmtK(currELSS)}/month`;
                                                        elssWhen = "Ongoing";
                                                    } else if (currELSS > 0) {
                                                        elssAction = `Increase ${prefix}ELSS SIP to ₹${fmtK(targetELSS)}/month (current: ₹${fmtK(currELSS)})`;
                                                        elssWhen = "Month 2";
                                                    } else {
                                                        elssAction = `Start ${prefix}ELSS SIP of ₹${fmtK(targetELSS)}/month`;
                                                        elssWhen = "Month 2";
                                                    }
                                                }

                                                const hNPS = safeParse(sD.husbandNPS) || 0;
                                                const wNPS = safeParse(sD.wifeNPS) || 0;
                                                let npsAction = "";
                                                let npsWhen = "";
                                                let npsWho = "";
                                                if (combinedMonthly === 0) {
                                                    npsAction = "No NPS contribution recommended (No active income)";
                                                    npsWhen = "N/A";
                                                    npsWho = "N/A";
                                                } else {
                                                    if (hTotalInc > 0 && wTotalInc > 0) {
                                                        if (hNPS >= 4166 && wNPS >= 4166) {
                                                            npsAction = `Maintain NPS of ₹4.2k/month each`;
                                                            npsWhen = "Ongoing";
                                                            npsWho = "Both";
                                                        } else {
                                                            const parts = [];
                                                            if (hNPS < 4166) parts.push(`${hFirst}: ₹${fmtK(hNPS)}→₹4.2k`);
                                                            if (wNPS < 4166) parts.push(`${wFirst}: ₹${fmtK(wNPS)}→₹4.2k`);
                                                            npsAction = `Increase NPS to ₹4.2k/month (${parts.join(', ')})`;
                                                            npsWhen = "Month 2";
                                                            npsWho = "Both";
                                                        }
                                                    } else if (hTotalInc > 0) {
                                                        if (hNPS >= 4166) {
                                                            npsAction = `Maintain NPS of ₹4.2k/month for ${hFirst}`;
                                                            npsWhen = "Ongoing";
                                                            npsWho = hFirst;
                                                        } else {
                                                            npsAction = `Start NPS of ₹4.2k/month for ${hFirst} (current: ₹${fmtK(hNPS)})`;
                                                            npsWhen = "Month 2";
                                                            npsWho = hFirst;
                                                        }
                                                    } else {
                                                        if (wNPS >= 4166) {
                                                            npsAction = `Maintain NPS of ₹4.2k/month for ${wFirst}`;
                                                            npsWhen = "Ongoing";
                                                            npsWho = wFirst;
                                                        } else {
                                                            npsAction = `Start NPS of ₹4.2k/month for ${wFirst} (current: ₹${fmtK(wNPS)})`;
                                                            npsWhen = "Month 2";
                                                            npsWho = wFirst;
                                                        }
                                                    }
                                                }

                                                const ccOS = safeParse(sF.creditCardOutstanding) || 0;
                                                let plAction = "";
                                                let plWhen = "";
                                                let plWho = "";
                                                if (combinedMonthly === 0) {
                                                    plAction = "No surplus routing recommended (No active income)";
                                                    plWhen = "N/A";
                                                    plWho = "N/A";
                                                } else {
                                                    const whoRole = (hTotalInc > 0 && wTotalInc > 0) ? "Joint" : (hTotalInc > 0 ? hFirst : wFirst);
                                                    plWho = whoRole;
                                                    if (ccOS > 0) {
                                                        plAction = `Pay off credit card outstanding of ₹${fmt(ccOS)} immediately`;
                                                        plWhen = "This week";
                                                    } else if (pLoan > 0) {
                                                        const extraPL = Math.max(10000, Math.round(pLoan * 0.5 / 1000) * 1000);
                                                        plAction = `Pay ₹${fmtK(extraPL)} extra on personal loan monthly (current: ₹${fmtK(pLoan)})`;
                                                        plWhen = "Month 1-10";
                                                    } else if (cLoan > 0) {
                                                        const extraCL = Math.max(5000, Math.round(cLoan * 0.3 / 1000) * 1000);
                                                        plAction = `Pay ₹${fmtK(extraCL)} extra on car loan monthly (current: ₹${fmtK(cLoan)})`;
                                                        plWhen = "Month 1-12";
                                                    } else if (hLoan > 0) {
                                                        const extraHL = Math.max(5000, Math.round(hLoan * 0.2 / 1000) * 1000);
                                                        plAction = `Pay ₹${fmtK(extraHL)} extra on home loan monthly (current: ₹${fmtK(hLoan)})`;
                                                        plWhen = "Month 1-12";
                                                    } else {
                                                        plAction = "Route monthly surplus to wealth creation (Equity SIPs)";
                                                        plWhen = "Month 1";
                                                    }
                                                }

                                                let milestoneAction = "";
                                                let milestoneWhen = "";
                                                let milestoneWho = "";
                                                if (combinedMonthly === 0) {
                                                    milestoneAction = "Review goals and explore income options";
                                                    milestoneWhen = "Ongoing";
                                                    milestoneWho = "Both";
                                                } else {
                                                    const whoRole = (hTotalInc > 0 && wTotalInc > 0) ? "Joint" : (hTotalInc > 0 ? hFirst : wFirst);
                                                    milestoneWho = whoRole;
                                                    if (ccOS > 0) {
                                                        milestoneAction = "Clear credit card debt and close accounts";
                                                        milestoneWhen = "Month 2";
                                                    } else if (pLoan > 0) {
                                                        milestoneAction = `Close personal loan entirely`;
                                                        milestoneWhen = "Month 10";
                                                        milestoneWho = "Celebrate!";
                                                    } else if (cLoan > 0) {
                                                        milestoneAction = "Prepay car loan to reduce tenure";
                                                        milestoneWhen = "Month 12";
                                                    } else if (hLoan > 0) {
                                                        milestoneAction = "Prepay 5% home loan principal annually";
                                                        milestoneWhen = "Year 1";
                                                    } else {
                                                        milestoneAction = "Build a ₹5L tax-free investment corpus";
                                                        milestoneWhen = "Month 12";
                                                    }
                                                }

                                                const efTotal = tEF > 0 ? `₹${formatCover(tEF)}` : "₹6L";
                                                let efTargetAction = "";
                                                let efTargetWhen = "";
                                                let efTargetWho = "";
                                                if (combinedMonthly === 0) {
                                                    efTargetAction = "No emergency fund target (No active income)";
                                                    efTargetWhen = "N/A";
                                                    efTargetWho = "N/A";
                                                } else {
                                                    efTargetAction = `Emergency fund reaches ${efTotal}`;
                                                    efTargetWhen = "Month 12";
                                                    efTargetWho = "Redirect to SIPs";
                                                }

                                                let sipAction = "";
                                                let sipWhen = "";
                                                let sipWho = "";
                                                if (combinedMonthly === 0) {
                                                    sipAction = "No SIP increase recommended (No active income)";
                                                    sipWhen = "N/A";
                                                    sipWho = "N/A";
                                                } else {
                                                    const whoRole = (hTotalInc > 0 && wTotalInc > 0) ? "Joint" : (hTotalInc > 0 ? hFirst : wFirst);
                                                    sipWho = whoRole;
                                                    const surplus = combinedMonthly - jExpenses - currELSS;
                                                    const targetSIPVal = currELSS + (safeParse(surplus) || 0) + (pLoan || cLoan || 0);
                                                    const futureSIPVal = targetSIPVal > 0 ? targetSIPVal : Math.max(50000, Math.round(combinedMonthly * 0.35 / 10000) * 10000);
                                                    sipAction = `Increase SIP to ₹${fmtK(futureSIPVal)}/month`;
                                                    sipWhen = "Year 2";
                                                }

                                                return (
                                                    <>
                                                        <tr>
                                                            <td style={{ textAlign: 'center' }}><span className="checklist-checkbox"></span></td>
                                                            <td style={{ fontWeight: '700' }}>{hTermAction}</td>
                                                            <td>{hTermWhen}</td>
                                                            <td>{hTermWho}</td>
                                                        </tr>
                                                        <tr>
                                                            <td style={{ textAlign: 'center' }}><span className="checklist-checkbox"></span></td>
                                                            <td style={{ fontWeight: '700' }}>{wTermAction}</td>
                                                            <td>{wTermWhen}</td>
                                                            <td>{wTermWho}</td>
                                                        </tr>
                                                        <tr>
                                                            <td style={{ textAlign: 'center' }}><span className="checklist-checkbox"></span></td>
                                                            <td style={{ fontWeight: '700' }}>{efAction}</td>
                                                            <td>{efWhen}</td>
                                                            <td>{efWho}</td>
                                                        </tr>
                                                        <tr>
                                                            <td style={{ textAlign: 'center' }}><span className="checklist-checkbox"></span></td>
                                                            <td style={{ fontWeight: '700' }}>{taxAction}</td>
                                                            <td>{taxWhen}</td>
                                                            <td>{taxWho}</td>
                                                        </tr>
                                                        <tr>
                                                            <td style={{ textAlign: 'center' }}><span className="checklist-checkbox"></span></td>
                                                            <td style={{ fontWeight: '700' }}>{hrAction}</td>
                                                            <td>{hrWhen}</td>
                                                            <td>{hrWho}</td>
                                                        </tr>
                                                        <tr>
                                                            <td style={{ textAlign: 'center' }}><span className="checklist-checkbox"></span></td>
                                                            <td style={{ fontWeight: '700' }}>{ppfAction}</td>
                                                            <td>{ppfWhen}</td>
                                                            <td>{ppfWho}</td>
                                                        </tr>
                                                        <tr>
                                                            <td style={{ textAlign: 'center' }}><span className="checklist-checkbox"></span></td>
                                                            <td style={{ fontWeight: '700' }}>{elssAction}</td>
                                                            <td>{elssWhen}</td>
                                                            <td>{elssWho}</td>
                                                        </tr>
                                                        <tr>
                                                            <td style={{ textAlign: 'center' }}><span className="checklist-checkbox"></span></td>
                                                            <td style={{ fontWeight: '700' }}>{npsAction}</td>
                                                            <td>{npsWhen}</td>
                                                            <td>{npsWho}</td>
                                                        </tr>
                                                        <tr>
                                                            <td style={{ textAlign: 'center' }}><span className="checklist-checkbox"></span></td>
                                                            <td style={{ fontWeight: '700' }}>{plAction}</td>
                                                            <td>{plWhen}</td>
                                                            <td>{plWho}</td>
                                                        </tr>
                                                        <tr>
                                                            <td style={{ textAlign: 'center' }}><span className="checklist-checkbox"></span></td>
                                                            <td style={{ fontWeight: '700' }}>{milestoneAction}</td>
                                                            <td>{milestoneWhen}</td>
                                                            <td style={milestoneWho === "Celebrate!" ? { color: '#069668', fontWeight: '800' } : {}}>{milestoneWho}</td>
                                                        </tr>
                                                        <tr>
                                                            <td style={{ textAlign: 'center' }}><span className="checklist-checkbox"></span></td>
                                                            <td style={{ fontWeight: '700' }}>{efTargetAction}</td>
                                                            <td>{efTargetWhen}</td>
                                                            <td>{efTargetWho}</td>
                                                        </tr>
                                                        <tr>
                                                            <td style={{ textAlign: 'center' }}><span className="checklist-checkbox"></span></td>
                                                            <td style={{ fontWeight: '700' }}>{sipAction}</td>
                                                            <td>{sipWhen}</td>
                                                            <td>{sipWho}</td>
                                                        </tr>
                                                    </>
                                                );
                                            })()}
                                        </tbody>
                                    </table>
                                </div>
                            ) : (
                                <div className="checklist-container">
                                    <table className="checklist-table">
                                        <thead>
                                            <tr>
                                                <th style={{ width: '40px', textAlign: 'center' }}>
                                                    <span className="checklist-checkbox" style={{ display: 'inline-block' }}></span>
                                                </th>
                                                <th><span className="checklist-badge">Action</span></th>
                                                <th><span className="checklist-badge">By When</span></th>
                                                <th><span className="checklist-badge">Impact</span></th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {(() => {
                                                const formatCover = (val) => {
                                                    if (val >= 10000000) return `${(val / 10000000).toFixed(1).replace(/\.0$/, '')} Crore`;
                                                    return `${Math.round(val / 100000)} Lakh`;
                                                };

                                                const isBiz = data.personalInfo?.incomeType === 'business' || calculations?.incomeType === 'business';
                                                const sInc = (isBiz ? data.businessDetails : data.salaryIncome) || {};
                                                const sInv = (isBiz ? data.businessInvestment : data.salaryInvestment) || {};
                                                
                                                const monthlyIncome = safeParse(data.personalInfo?.monthlySalary) || metrics?.totalMonthlyIncome || 0;
                                                const annualGrossSalary = monthlyIncome * 12;
                                                const idealLifeCover = annualGrossSalary * 10;
                                                const currentLifeCover = safeParse(metrics.currentLifeCover) || 0;
                                                
                                                const actualLoanEMI = safeParse(sInc.personalLoanEMI) || 0;

                                                const fmt = (v) => Math.round(v || 0).toLocaleString('en-IN');

                                                // 1. Term Insurance
                                                let termAction = "";
                                                let termWhen = "";
                                                let termImpact = "";
                                                if (monthlyIncome === 0 && currentLifeCover === 0) {
                                                    termAction = "No term insurance needed (No active income)";
                                                    termWhen = "N/A";
                                                    termImpact = "N/A";
                                                } else if (currentLifeCover >= idealLifeCover) {
                                                    termAction = `Maintain Term Life Insurance of ₹${formatCover(currentLifeCover)}`;
                                                    termWhen = "Ongoing";
                                                    termImpact = "Adequate — maintains safety net";
                                                } else {
                                                    termAction = currentLifeCover > 0
                                                        ? `Increase Term Life Insurance by ₹${formatCover(idealLifeCover - currentLifeCover)} (target ₹${formatCover(idealLifeCover)})`
                                                        : `Buy ₹${formatCover(idealLifeCover)} Term Life Insurance`;
                                                    termWhen = "This Week";
                                                    termImpact = "Critical — closes cover gap";
                                                }

                                                // 2. Emergency Fund
                                                const targetEmergency = metrics?.targetEmergency || (metrics?.totalEssentialExpenses * 6) || 0;
                                                const currentEmergency = metrics?.currentEmergency || 0;
                                                const eGap = Math.max(0, targetEmergency - currentEmergency);
                                                const efMonthly = eGap > 0 ? Math.max(1000, Math.round(eGap / 18 / 1000) * 1000) : 0;

                                                let efAction = "";
                                                let efWhen = "";
                                                let efImpact = "";
                                                if (monthlyIncome === 0) {
                                                    efAction = "No emergency fund target (No active income)";
                                                    efWhen = "N/A";
                                                    efImpact = "N/A";
                                                } else if (eGap === 0) {
                                                    efAction = `Maintain emergency fund reserve of ₹${formatCover(targetEmergency)}`;
                                                    efWhen = "Ongoing";
                                                    efImpact = "Adequate — safety net fully funded";
                                                } else {
                                                    efAction = `Set up ₹${fmt(efMonthly)}/month Emergency Fund auto-transfer`;
                                                    efWhen = "This Week";
                                                    efImpact = "Urgent — builds safety net";
                                                }

                                                // 3. Tax Regime
                                                const recRegime = metrics?.tax?.recommendedRegime || data.taxDetails?.taxRegime || "Old";
                                                const taxSavingsAnn = safeParse(metrics?.tax?.taxSavings) || 0;

                                                let taxAction = "";
                                                let taxWhen = "";
                                                let taxImpact = "";
                                                if (monthlyIncome === 0) {
                                                    taxAction = "No tax regime declaration needed (No active income)";
                                                    taxWhen = "N/A";
                                                    taxImpact = "N/A";
                                                } else if (isBiz) {
                                                    taxAction = `Adopt ${recRegime.charAt(0).toUpperCase() + recRegime.slice(1)} Tax Regime for business filing`;
                                                    taxWhen = "Filing deadline";
                                                    taxImpact = taxSavingsAnn > 0 ? `Saves ₹${fmt(taxSavingsAnn)}/yr on tax liability` : "Optimizes tax liability";
                                                } else {
                                                    taxAction = `Declare ${recRegime.charAt(0).toUpperCase() + recRegime.slice(1)} Tax Regime to HR`;
                                                    taxWhen = "This Week";
                                                    taxImpact = taxSavingsAnn > 0 ? `Saves ₹${fmt(taxSavingsAnn)}/yr immediately` : "Optimizes tax liability";
                                                }

                                                // 4. PPF
                                                const currentPPF = safeParse(sInv.ppf) || safeParse(sInc.ppf) || metrics?.ppfMonthly || 0;
                                                let ppfAction = "";
                                                let ppfWhen = "";
                                                let ppfImpact = "";
                                                if (monthlyIncome === 0) {
                                                    ppfAction = "No PPF contribution recommended (No active income)";
                                                    ppfWhen = "N/A";
                                                    ppfImpact = "N/A";
                                                } else if (currentPPF >= 12500) {
                                                    ppfAction = `Maintain PPF contribution of ₹12.5k/month`;
                                                    ppfWhen = "Ongoing";
                                                    ppfImpact = "Maxes 80C + retirement corpus";
                                                } else {
                                                    ppfAction = currentPPF > 0
                                                        ? `Increase PPF to ₹12.5k/month (current: ₹${fmt(currentPPF)})`
                                                        : `Start PPF contribution of ₹12.5k/month`;
                                                    ppfWhen = "This Month";
                                                    ppfImpact = "Maxes 80C + retirement corpus";
                                                }

                                                // 5. SIP
                                                const currentSIP = safeParse(sInv.mutualFunds) || safeParse(sInv.stocksEquity) || metrics?.sipMonthly || 0;
                                                const targetSIP = monthlyIncome > 0 ? Math.max(10000, Math.round(monthlyIncome * 0.15 / 1000) * 1000) : 0;
                                                let sipAction = "";
                                                let sipWhen = "";
                                                let sipImpact = "";
                                                if (monthlyIncome === 0) {
                                                    sipAction = "No ELSS SIP recommended (No active income)";
                                                    sipWhen = "N/A";
                                                    sipImpact = "N/A";
                                                } else if (currentSIP >= targetSIP) {
                                                    sipAction = `Maintain ELSS SIP of ₹${fmt(currentSIP)}/month`;
                                                    sipWhen = "Ongoing";
                                                    sipImpact = "Tax saving + long-term wealth";
                                                } else {
                                                    sipAction = currentSIP > 0
                                                        ? `Increase ELSS SIP to ₹${fmt(targetSIP)}/month (current: ₹${fmt(currentSIP)})`
                                                        : `Start ELSS SIP of ₹${fmt(targetSIP)}/month`;
                                                    sipWhen = "Month 2";
                                                    sipImpact = "Tax saving + long-term wealth";
                                                }

                                                // 6. NPS
                                                const currentNPS = safeParse(sInv.nps_80ccd1b) || safeParse(sInc.nps_80ccd1b) || metrics?.npsMonthly || 0;
                                                let npsAction = "";
                                                let npsWhen = "";
                                                let npsImpact = "";
                                                if (monthlyIncome === 0) {
                                                    npsAction = "No NPS contribution recommended (No active income)";
                                                    npsWhen = "N/A";
                                                    npsImpact = "N/A";
                                                } else if (currentNPS >= 4166) {
                                                    npsAction = `Maintain NPS contribution of ₹4.2k/month`;
                                                    npsWhen = "Ongoing";
                                                    npsImpact = "Extra ₹50,000 deduction + pension";
                                                } else {
                                                    npsAction = currentNPS > 0
                                                        ? `Increase NPS to ₹4.2k/month (current: ₹${fmt(currentNPS)})`
                                                        : `Open NPS and invest ₹4.2k/month`;
                                                    npsWhen = "Month 3";
                                                    npsImpact = "Extra ₹50,000 deduction + pension";
                                                }

                                                // 7. Personal Loan extra prepayment / Routing surplus
                                                let plAction = "";
                                                let plWhen = "";
                                                let plImpact = "";
                                                if (monthlyIncome === 0) {
                                                    plAction = "No surplus to route (No active income)";
                                                    plWhen = "N/A";
                                                    plImpact = "N/A";
                                                } else if (actualLoanEMI > 0) {
                                                    const plExtra = Math.max(5000, Math.round(actualLoanEMI * 0.5 / 1000) * 1000);
                                                    plAction = `Start ₹${fmt(plExtra)} extra personal loan prepayment`;
                                                    plWhen = "Month 1";
                                                    plImpact = "Save interest and reduce tenure";
                                                } else {
                                                    plAction = "Route surplus to equity mutual funds";
                                                    plWhen = "Month 1";
                                                    plImpact = "Accelerate long-term wealth creation";
                                                }

                                                // 8. Milestone
                                                let milestoneAction = "";
                                                let milestoneWhen = "";
                                                let milestoneImpact = "";
                                                if (monthlyIncome === 0) {
                                                    milestoneAction = "Explore new income opportunities and review goals";
                                                    milestoneWhen = "Ongoing";
                                                    milestoneImpact = "N/A";
                                                } else if (actualLoanEMI > 0) {
                                                    milestoneAction = "Close personal loan entirely";
                                                    milestoneWhen = "Month 12";
                                                    milestoneImpact = "Free cash flow for future goals";
                                                } else {
                                                    milestoneAction = "Build a ₹5L investment corpus";
                                                    milestoneWhen = "Month 12";
                                                    milestoneImpact = "Accelerate wealth creation";
                                                }

                                                // 9. HR declaration / Rent receipts
                                                const rentVal = safeParse(data.personalInfo?.rent) || 0;
                                                let hrAction = "";
                                                let hrWhen = "";
                                                let hrImpact = "";
                                                if (monthlyIncome === 0) {
                                                    hrAction = "No HR submission required (No active income)";
                                                    hrWhen = "N/A";
                                                    hrImpact = "N/A";
                                                } else if (isBiz) {
                                                    hrAction = rentVal > 0 ? "Keep rent receipts and invoices for business deductions" : "Document business expenses for deduction";
                                                    hrWhen = "Filing season";
                                                    hrImpact = "Reduce taxable business income";
                                                } else {
                                                    if (rentVal > 0) {
                                                        hrAction = "Submit rent receipts to HR (HRA)";
                                                        hrWhen = "Before deadline";
                                                        hrImpact = "Optimize HRA exemption";
                                                    } else {
                                                        hrAction = "Submit tax declarations to HR";
                                                        hrWhen = "Before deadline";
                                                        hrImpact = "Optimize tax deductions";
                                                    }
                                                }

                                                // 10. Emergency Fund target reached
                                                const efTotal = targetEmergency > 0 ? `₹${formatCover(targetEmergency)}` : "₹5 Lakh";
                                                let efTargetAction = "";
                                                let efTargetWhen = "";
                                                let efTargetImpact = "";
                                                if (monthlyIncome === 0) {
                                                    efTargetAction = "No emergency fund target (No active income)";
                                                    efTargetWhen = "N/A";
                                                    efTargetImpact = "N/A";
                                                } else {
                                                    efTargetAction = `Emergency fund reaches target (${efTotal})`;
                                                    efTargetWhen = "Month 18";
                                                    efTargetImpact = "Safety net fully funded";
                                                }

                                                return (
                                                    <>
                                                        <tr>
                                                            <td style={{ textAlign: 'center' }}><span className="checklist-checkbox"></span></td>
                                                            <td style={{ fontWeight: '700' }}>{termAction}</td>
                                                            <td>{termWhen}</td>
                                                            <td>{termImpact}</td>
                                                        </tr>
                                                        <tr>
                                                            <td style={{ textAlign: 'center' }}><span className="checklist-checkbox"></span></td>
                                                            <td style={{ fontWeight: '700' }}>{efAction}</td>
                                                            <td>{efWhen}</td>
                                                            <td>{efImpact}</td>
                                                        </tr>
                                                        <tr>
                                                            <td style={{ textAlign: 'center' }}><span className="checklist-checkbox"></span></td>
                                                            <td style={{ fontWeight: '700' }}>{taxAction}</td>
                                                            <td>{taxWhen}</td>
                                                            <td>{taxImpact}</td>
                                                        </tr>
                                                        <tr>
                                                            <td style={{ textAlign: 'center' }}><span className="checklist-checkbox"></span></td>
                                                            <td style={{ fontWeight: '700' }}>{ppfAction}</td>
                                                            <td>{ppfWhen}</td>
                                                            <td>{ppfImpact}</td>
                                                        </tr>
                                                        <tr>
                                                            <td style={{ textAlign: 'center' }}><span className="checklist-checkbox"></span></td>
                                                            <td style={{ fontWeight: '700' }}>{sipAction}</td>
                                                            <td>{sipWhen}</td>
                                                            <td>{sipImpact}</td>
                                                        </tr>
                                                        <tr>
                                                            <td style={{ textAlign: 'center' }}><span className="checklist-checkbox"></span></td>
                                                            <td style={{ fontWeight: '700' }}>{npsAction}</td>
                                                            <td>{npsWhen}</td>
                                                            <td>{npsImpact}</td>
                                                        </tr>
                                                        <tr>
                                                            <td style={{ textAlign: 'center' }}><span className="checklist-checkbox"></span></td>
                                                            <td style={{ fontWeight: '700' }}>{plAction}</td>
                                                            <td>{plWhen}</td>
                                                            <td>{plImpact}</td>
                                                        </tr>
                                                        <tr>
                                                            <td style={{ textAlign: 'center' }}><span className="checklist-checkbox"></span></td>
                                                            <td style={{ fontWeight: '700' }}>{milestoneAction}</td>
                                                            <td>{milestoneWhen}</td>
                                                            <td>{milestoneImpact}</td>
                                                        </tr>
                                                        <tr>
                                                            <td style={{ textAlign: 'center' }}><span className="checklist-checkbox"></span></td>
                                                            <td style={{ fontWeight: '700' }}>{hrAction}</td>
                                                            <td>{hrWhen}</td>
                                                            <td>{hrImpact}</td>
                                                        </tr>
                                                        <tr>
                                                            <td style={{ textAlign: 'center' }}><span className="checklist-checkbox"></span></td>
                                                            <td style={{ fontWeight: '700' }}>{efTargetAction}</td>
                                                            <td>{efTargetWhen}</td>
                                                            <td>{efTargetImpact}</td>
                                                        </tr>
                                                    </>
                                                );
                                            })()}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                        <PageFooter num="14" />
                    </div>

                    {/* PAGE 15: SAVING STRATEGIES */}
                    <div className="report-page">
                        <PageHeader section="8" title="SAVING STRATEGIES" />
                        <div className="page-content-refined">
                            <h3>Winning the 80C Battle</h3>
                            <ul style={{ lineHeight: 2.2 }}>
                                <li>ELSS (Tax Saving Mutual Fund) - Best for Growth</li>
                                <li>NPS (National Pension Scheme) - Extra ₹50k Exemption</li>
                                <li>PPF - The Debt Companion</li>
                            </ul>
                        </div>
                        <PageFooter num="15" />
                    </div>

                    {/* PAGE 15: INFLATION */}
                    <div className="report-page">
                        <PageHeader section="7" title="INFLATION: THE TAX" />
                        <div className="page-content-refined">
                            <p style={{ fontSize: '24px', lineHeight: 1.8 }}>If your money doesn't grow at &gt; 8%, you are actually losing purchasing power every day. ₹1 Crore today will be worth only ₹25 Lakhs in 20 years.</p>
                        </div>
                        <PageFooter num="15" />
                    </div>

                    {/* PAGE 16: COST OF DELAY */}
                    <div className="report-page">
                        <PageHeader section="7" title="COST OF DELAY" />
                        <div className="page-content-refined">
                            <table className="premium-diagnosis-table">
                                <thead><tr><th>Delay</th><th>Value at 60</th></tr></thead>
                                <tbody>
                                    <tr><td>Start Today</td><td>₹3.4 Crore</td></tr>
                                    <tr><td>Wait 5 Years</td><td>₹1.8 Crore</td></tr>
                                </tbody>
                            </table>
                        </div>
                        <PageFooter num="16" />
                    </div>

                    {/* PAGE 17: RETIREMENT GOAL */}
                    <div className="report-page">
                        <PageHeader section="8" title="RETIREMENT PROJECTION" />
                        <div className="page-content-refined">
                            <div className="retirement-mega-box">
                                <label>TARGET CORPUS AT AGE 60</label>
                                <h2>₹{fmt(metrics.totalMonthlySavings * 300)}*</h2>
                            </div>
                        </div>
                        <PageFooter num="17" />
                    </div>

                    {/* PAGE 18: DO'S & DON'TS */}
                    <div className="report-page">
                        <PageHeader section="8" title="EXPERT DO'S & DON'TS" />
                        <div className="page-content-refined">
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '30px' }}>
                                <div><h4 style={{ color: '#1a7a5e' }}>✅ DO</h4><p>Automate SIPs.</p></div>
                                <div><h4 style={{ color: '#c0392b' }}>❌ DON'T</h4><p>Mix Insurance & Invest.</p></div>
                            </div>
                        </div>
                        <PageFooter num="18" />
                    </div>

                    {/* PAGE 19: ROADMAP */}
                    <div className="report-page final-roadmap-page">
                        <PageHeader section="9" title="90-DAY ROADMAP" />
                        <div className="page-content-refined">
                            {[
                                { icon: <MdSecurity />, title: "Survival Deposit", t: "Setup EF account." },
                                { icon: <MdShield />, title: "Term Shield", t: "₹1.5Cr Cover App." },
                                { icon: <MdTrendingUp />, title: "Automated SIP", t: "Nifty 50 Index Fund start." }
                            ].map((item, idx) => (
                                <div key={idx} className="roadmap-step-premium">
                                    <div className="step-icon-box">{item.icon}</div>
                                    <div className="step-desc"><h3>{item.title}</h3><p>{item.t}</p></div>
                                </div>
                            ))}
                        </div>
                        <PageFooter num="19" dark />
                    </div>
                </>
            )}

        </div>
    );
};

export default ModernReport;
